/* ============================================
   TFS Wheels — Merged-PDP Group Picker
   The "BUILD YOUR SET" buy card. Sells a wheel's
   whole size/offset/bolt-pattern group from one
   PDP. Fetches GET /api/fitment/group (via
   TFSFitment.api, app-proxy fallback) and renders
   ONE card: card header, FRONT block (rich custom
   listbox — one row per group member — + the
   ADOPTED quantity stepper), and when the group is
   staggered-eligible an ALWAYS-VISIBLE optional
   REAR block (muted until a rear is picked; picking
   one shifts quantities 4 → 2+2, its Remove
   restores 4). Below: the ADOPTED price zone and
   the ADOPTED buy-buttons block (real
   <product-form> ATC) beside an "Add Tires" proxy
   for the existing cross-sell CTA.

   Adoption happens ONLY after a successful group
   fetch — if the API is down the server-rendered
   product page is left completely untouched.

   Payload contract: docs/specs/2026-07-02-pdp-overhaul-phase0-plan.md (Task 6).
   Prior behavioral contract: docs/specs/2026-07-02-pdp-overhaul-phase1-plan.md (Task 5);
   this file supersedes its button-gated staggered mode with the
   visible-by-default optional rear block (owner design round, 2026-07).

   TIRES (Phase 3): the same card sells tire size runs. The /group payload
   carries productType ('wheels'|'tires'); tire members carry the size via
   ts (with tireSize as the wire name today) plus sku/price/cap/qty/ti/sn/
   pkg/fit, and li (load index) / lr (load range) once the backend ships
   them — every tire field is read defensively. Tire rows render bold ts
   with a "Load 121 · Range E" sub-line, the meta row keeps the PART #
   copy button and drops BORE, and the staggered opener adds rear TIRES:
   any other size qualifies as a rear without a vehicle (buyers know their
   sizes); with a vehicle the member's rear verdict gates it (no
   custom-drill exemption — N/A for tires). Qty 2+2 mechanics, pricing and
   the 2-POST cart add are identical to wheels.

   SIBLING (per-size) pages (2026-07-03): the root carries
   data-mode="sibling" and the SAME card runs locked to the page product —
   a static size row replaces the front listbox (the H1 already names the
   size), the staggered pool is scoped by siblingScope() (page bolt
   pattern for wheels / rim diameter for tires), the opener only shows
   when real rear candidates exist, and there is no auto-select, no
   pushState, no giant-group mode. Everything else — adoption (price
   below the block, Add Tires beside ATC, buy-foot delivery + pkg hook),
   2+2 splitting, verdict stamping, the 2-POST staggered add — is the
   hub code path unchanged. Spec:
   docs/specs/2026-07-03-spec-page-staggered-buybox.md

   PAGE-AS-REAR sibling mode (2026-08-20): the original sibling gate hid
   the opener on the WIDEST width of a group (no strictly-wider rear
   exists), so those pages could never start a staggered set. When
   siblingStaggerPlan finds no rear candidates but narrower same-
   diameter/same-pattern fronts exist, the SAME slot sells the FRONT
   axle instead: the locked page product becomes the REAR, every axle
   word flips (opener, slot heading, hint, listbox placeholder, qty
   labels, set-total lines), and state.pageAsRear maps the axles at the
   boundaries — the 2-POST add still posts front-first with per-axle
   verdict stamps, and the selection contract's front/rear entries swap
   so slot-grammar consumers stay truthful. Spec:
   docs/specs/2026-08-20-sibling-page-as-rear-staggered.md
   ============================================ */
(function () {
  'use strict';

  var GIANT_GROUP_THRESHOLD = 100;
  var FITMENT_READY_TIMEOUT_MS = 3000;

  /* Verdicts a vehicle-filtered listbox may show. does_not_fit / no_data rows
     are removed entirely (the fitment widget can reveal them by dispatching
     `tfs:fitment-filter` with detail {showAll:true} — the card itself renders
     zero fitment UI). */
  var FIT_ALLOWED = {
    universal_fit: 1,
    direct_fit: 1,
    aftermarket_fit: 1,
    modification_required: 1
  };

  /* Auto-select priority tiers (lower wins). universal counts as direct. */
  var FIT_TIER = {
    direct_fit: 0,
    universal_fit: 0,
    aftermarket_fit: 1,
    modification_required: 2
  };

  /* ---------------------------------------------------------------
     Pure helpers (unit-tested in server/__tests__/pdpGroupPicker.helpers.test.mjs)
     --------------------------------------------------------------- */

  function num(v) {
    var n = Number(v);
    return (v == null || isNaN(n)) ? 0 : n;
  }

  function memberAvailable(m) {
    return !!m && (m.ti === 0 || m.qty > 0);
  }

  /* Tire size string for a member — the /group payload's wire name is
     tireSize; ts is the compact name the metafield payload (and a future
     wire rename) uses. '' for wheels/missing. */
  function tireSizeOf(m) {
    if (!m) return '';
    if (m.ts != null && String(m.ts) !== '') return String(m.ts);
    if (m.tireSize != null && String(m.tireSize) !== '') return String(m.tireSize);
    return '';
  }

  /* Rim diameter parsed out of a tire size ("285/70R17" → 17); 0 when
     unparseable so unknown sizes sort first, never throw. */
  function tireRimOf(ts) {
    var match = /R\s*(\d+(?:\.\d+)?)/i.exec(String(ts));
    return match ? Number(match[1]) : 0;
  }

  /* Selection-contract dia/width (Stage 3 Task 1): wheels carry d/w
     directly; tires only carry a size string — dia is the parsed rim
     (cheap, already available via tireRimOf), width has no cheap
     equivalent (a tire's section width isn't a single "fits this wheel
     width" number) so it stays null rather than guessing. null (not 0)
     when unparseable/absent — 0 is a real rim value's sort fallback
     above, not a "no data" signal here. */
  function memberDia(m) {
    if (!m) return null;
    if (m.d != null) return num(m.d);
    var ts = tireSizeOf(m);
    if (!ts) return null;
    var rim = tireRimOf(ts);
    return rim > 0 ? rim : null;
  }

  function memberWidth(m) {
    if (!m) return null;
    return m.w != null ? num(m.w) : null;
  }

  /* Flat listbox rows — wheels: diameter asc, width asc, offset asc (price
     tiebreak). Tires (both rows carry a size string): rim diameter asc,
     then the size string (numeric-aware), then price. Sold-out members keep
     their spec-sorted slot — they stay selectable for the back-in-stock
     flow, no availability reordering. */
  function buildRows(members) {
    var rows = (members || []).filter(function (m) { return !!m; });
    rows.sort(function (a, b) {
      var ats = tireSizeOf(a);
      var bts = tireSizeOf(b);
      if (ats && bts) {
        var rimGap = tireRimOf(ats) - tireRimOf(bts);
        if (rimGap) return rimGap;
        if (ats !== bts) {
          try {
            var lc = ats.localeCompare(bts, undefined, { numeric: true });
            if (lc) return lc;
          } catch (e) {
            if (ats < bts) return -1;
            if (ats > bts) return 1;
          }
        }
        return num(a.price) - num(b.price);
      }
      if (num(a.d) !== num(b.d)) return num(a.d) - num(b.d);
      if (num(a.w) !== num(b.w)) return num(a.w) - num(b.w);
      if (num(a.o) !== num(b.o)) return num(a.o) - num(b.o);
      return num(a.price) - num(b.price);
    });
    return rows;
  }

  /* Staggered CTA gate — wheels: the group offers ≥2 distinct widths at one
     diameter. Tires: ≥2 distinct sizes (buyers pick any front/rear combo). */
  function staggeredEligible(members) {
    var widthsByDia = {};
    var list = members || [];
    var i;
    var tireSizes = {};
    var tireCount = 0;
    for (i = 0; i < list.length; i++) {
      var t = tireSizeOf(list[i]);
      if (!t) continue;
      if (!Object.prototype.hasOwnProperty.call(tireSizes, t)) {
        tireSizes[t] = 1;
        tireCount++;
        if (tireCount >= 2) return true;
      }
    }
    for (i = 0; i < list.length; i++) {
      var m = list[i];
      if (!m || m.d == null || m.w == null) continue;
      var key = String(m.d);
      if (!widthsByDia[key]) widthsByDia[key] = {};
      widthsByDia[key][String(m.w)] = 1;
    }
    for (var d in widthsByDia) {
      if (!Object.prototype.hasOwnProperty.call(widthsByDia, d)) continue;
      var count = 0;
      for (var w in widthsByDia[d]) {
        if (Object.prototype.hasOwnProperty.call(widthsByDia[d], w)) count++;
      }
      if (count >= 2) return true;
    }
    return false;
  }

  function fitTier(m) {
    var overall = m && m.fit && m.fit.overall;
    return (overall != null && FIT_TIER[overall] != null) ? FIT_TIER[overall] : 3;
  }

  /* Best fitting member for auto-select: direct/universal > aftermarket >
     mods; in-stock first within a tier; nearest diameter to the previous
     selection; then cheapest. Returns null on an empty list. */
  function bestFit(members, prevSelection) {
    var list = members || [];
    var best = null;
    function cmp(a, b) {
      var t = fitTier(a) - fitTier(b);
      if (t) return t;
      var av = (memberAvailable(a) ? 0 : 1) - (memberAvailable(b) ? 0 : 1);
      if (av) return av;
      if (prevSelection && prevSelection.d != null) {
        var ga = Math.abs(num(a.d) - num(prevSelection.d));
        var gb = Math.abs(num(b.d) - num(prevSelection.d));
        if (ga !== gb) return ga - gb;
      }
      return num(a.price) - num(b.price);
    }
    for (var i = 0; i < list.length; i++) {
      if (!list[i]) continue;
      if (!best || cmp(list[i], best) < 0) best = list[i];
    }
    return best;
  }

  /* Vehicle-mode hard filter: fitting verdicts plus ALL custom-drill members
     (cd=1 fits by construction). Members without a verdict are kept
     (fail-open). showAll bypasses and returns a copy. */
  function filterByFit(members, showAll) {
    var list = members || [];
    if (showAll) return list.slice();
    var out = [];
    list.forEach(function (m) {
      if (!m) return;
      if (m.cd) { out.push(m); return; }
      if (!m.fit || !m.fit.overall) { out.push(m); return; }
      if (FIT_ALLOWED[m.fit.overall]) out.push(m);
    });
    return out;
  }

  /* Rear-axle listbox candidates for a staggered setup.
     WHEELS: same diameter AND same bolt pattern as the front pick (one car,
     one pattern — custom-drill on either axle is exempt since it drills to
     pattern; a member without a pattern fails closed, matching the
     width/diameter rules; an unknown FRONT pattern can't scope and skips the
     check). Without a vehicle the rear must additionally be strictly wider
     than the front; with a vehicle the member's REAR verdict must be allowed
     (custom-drill always allowed; a missing rear verdict fails open — the
     bolt-pattern check above still closes cross-pattern leaks there).
     TIRES (the front pick carries a size string): no width/diameter
     ordering — buyers know their sizes. Without a vehicle every OTHER size
     qualifies; with a vehicle the member's REAR verdict must be allowed
     (no custom-drill exemption — N/A for tires; a missing rear verdict
     fails open). The front pick itself is NEVER a rear candidate in any
     mode (2026-08-20 symmetry cleanup — the vehicle branch used to echo
     it back: same dia/bp with its own rear verdict passing, a square set
     of the same product sold through the staggered UI; frontOptionsFor
     below shipped with the exclusion and this now matches). Returned
     buildRows-sorted. */
  function rearOptionsFor(front, members, vehicleOn) {
    var frontTs = tireSizeOf(front);
    if (frontTs) {
      var tireOut = [];
      (members || []).forEach(function (m) {
        if (!m) return;
        var ts = tireSizeOf(m);
        if (!ts) return;
        if (front.pid != null && m.pid === front.pid) return;
        if (vehicleOn) {
          var rearFit = m.fit && m.fit.rear;
          if (rearFit && !FIT_ALLOWED[rearFit]) return;
        }
        tireOut.push(m);
      });
      return buildRows(tireOut);
    }
    if (!front || front.d == null) return [];
    var frontBp = (!front.cd && front.bp != null && String(front.bp) !== '') ? String(front.bp) : null;
    var out = [];
    (members || []).forEach(function (m) {
      if (!m || m.d == null) return;
      if (front.pid != null && m.pid === front.pid) return;
      if (num(m.d) !== num(front.d)) return;
      if (frontBp && !m.cd && (m.bp == null || String(m.bp) !== frontBp)) return;
      if (vehicleOn) {
        if (!m.cd) {
          var rear = m.fit && m.fit.rear;
          if (rear && !FIT_ALLOWED[rear]) return;
        }
      } else {
        if (m.w == null || front.w == null) return;
        if (num(m.w) <= num(front.w)) return;
      }
      out.push(m);
    });
    return buildRows(out);
  }

  /* Front-axle listbox candidates for a staggered setup whose REAR is
     locked (page-as-rear sibling mode, owner 2026-08-20). Axle mirror of
     rearOptionsFor:
     WHEELS: same diameter AND same bolt pattern as the locked rear
     (custom-drill exempt on either axle; a member without a pattern fails
     closed; an unknown REAR pattern can't scope and skips the check).
     Without a vehicle the front must be strictly NARROWER than the rear;
     with a vehicle the member's FRONT verdict must be allowed
     (custom-drill always allowed; a missing front verdict fails open).
     TIRES: without a vehicle every OTHER size qualifies; with a vehicle
     the FRONT verdict gates it (no custom-drill exemption — N/A).
     The locked rear itself is NEVER offered as a front — pairing a
     product with itself is a square set the native ATC already sells.
     rearOptionsFor applies the same self-exclusion (2026-08-20 symmetry
     cleanup), so the two pool helpers are exact axle mirrors. Returned
     buildRows-sorted. */
  function frontOptionsFor(rear, members, vehicleOn) {
    var rearTs = tireSizeOf(rear);
    if (rearTs) {
      var tireOut = [];
      (members || []).forEach(function (m) {
        if (!m) return;
        var ts = tireSizeOf(m);
        if (!ts) return;
        if (rear.pid != null && m.pid === rear.pid) return;
        if (vehicleOn) {
          var frontFit = m.fit && m.fit.front;
          if (frontFit && !FIT_ALLOWED[frontFit]) return;
        }
        tireOut.push(m);
      });
      return buildRows(tireOut);
    }
    if (!rear || rear.d == null) return [];
    var rearBp = (!rear.cd && rear.bp != null && String(rear.bp) !== '') ? String(rear.bp) : null;
    var out = [];
    (members || []).forEach(function (m) {
      if (!m || m.d == null) return;
      if (rear.pid != null && m.pid === rear.pid) return;
      if (num(m.d) !== num(rear.d)) return;
      if (rearBp && !m.cd && (m.bp == null || String(m.bp) !== rearBp)) return;
      if (vehicleOn) {
        if (!m.cd) {
          var front = m.fit && m.fit.front;
          if (front && !FIT_ALLOWED[front]) return;
        }
      } else {
        if (m.w == null || rear.w == null) return;
        if (num(m.w) >= num(rear.w)) return;
      }
      out.push(m);
    });
    return buildRows(out);
  }

  /* Sibling (per-size) page candidate scope. The page product is a fixed
     front, so the staggered pool is locked to what can pair with it:
     WHEELS — members sharing the locked product's bolt pattern (custom-drill
     members pass, they drill to pattern; a member without a pattern fails
     closed). A locked product with no pattern (or custom-drill) can't scope
     → copy of the full list.
     TIRES — members on the locked tire's rim diameter (unparseable member
     sizes fail closed; an unparseable locked size can't scope → full copy).
     The locked member itself is always kept. */
  function siblingScope(members, locked) {
    var list = (members || []).filter(function (m) { return !!m; });
    if (!locked) return list;
    var lockedTs = tireSizeOf(locked);
    if (lockedTs) {
      var rim = tireRimOf(lockedTs);
      if (!rim) return list;
      return list.filter(function (m) {
        if (locked.pid != null && m.pid === locked.pid) return true;
        return tireRimOf(tireSizeOf(m)) === rim;
      });
    }
    if (locked.cd || locked.bp == null || String(locked.bp) === '') return list;
    var bp = String(locked.bp);
    return list.filter(function (m) {
      if (locked.pid != null && m.pid === locked.pid) return true;
      if (m.cd) return true;
      return m.bp != null && String(m.bp) === bp;
    });
  }

  /* Which axle does the sibling page's staggered slot sell? (owner
     2026-08-20 — "build a staggered set from any PDP"). Rear picks win
     when they exist — the page product stays the FRONT, matching the
     shipped behavior everywhere it already worked. Only when the page
     product has NO rear candidates (it's the widest width at its
     diameter/pattern — or, with a vehicle, every rear verdict is
     blocked) does the slot flip to selling FRONTS with the page product
     locked as the REAR (pageAsRear). Both pools empty → no opener at
     all, exactly as before. rows come back buildRows-sorted from the
     underlying pool helpers. */
  function siblingStaggerPlan(locked, members, vehicleOn) {
    if (!locked) return { pageAsRear: false, rows: [] };
    var rearRows = rearOptionsFor(locked, members, vehicleOn);
    if (rearRows.length) return { pageAsRear: false, rows: rearRows };
    var frontRows = frontOptionsFor(locked, members, vehicleOn);
    if (frontRows.length) return { pageAsRear: true, rows: frontRows };
    return { pageAsRear: false, rows: [] };
  }

  /* "+$50.00" / "−$25.00" price delta from a signed cents amount. */
  function formatDelta(cents) {
    var n = Number(cents);
    if (cents == null || isNaN(n)) return '';
    n = Math.round(n);
    if (n === 0) return '';
    var abs = (Math.abs(n) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (n > 0 ? '+' : '−') + '$' + abs;
  }

  /* applySelection's initial early-return decision (owner bug 2026-08-19):
     the server-rendered page is only "already correct" when the initial
     selection IS the landing product. A filter-context preselect
     (selectPid / facet match) can resolve to a DIFFERENT member — that
     selection must re-render price/gallery/specs and the ATC variant input
     exactly like a manual pick, or the page shows the landing product's
     price and adds the landing product's variant to cart while the
     dropdown claims the preselected sibling. Missing pids fail toward
     re-rendering (idempotent) — never toward the stale-page bug. */
  function initialRenderAlreadyCorrect(initial, memberPid, landingPid) {
    return !!initial && memberPid != null && landingPid != null &&
      memberPid === landingPid;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      buildRows: buildRows,
      staggeredEligible: staggeredEligible,
      bestFit: bestFit,
      rearOptionsFor: rearOptionsFor,
      frontOptionsFor: frontOptionsFor,
      siblingStaggerPlan: siblingStaggerPlan,
      filterByFit: filterByFit,
      siblingScope: siblingScope,
      formatDelta: formatDelta,
      initialRenderAlreadyCorrect: initialRenderAlreadyCorrect
    };
  }

  /* Node/test environment: only the helpers above are wanted. */
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  /* ---------------------------------------------------------------
     Browser boot
     --------------------------------------------------------------- */

  if (window.__TFS_GROUP_PICKER__) return;
  window.__TFS_GROUP_PICKER__ = true;

  var root = document.getElementById('pdp-group-picker');
  if (!root) return;

  /* Grid click-through preselection (owner bug report 2026-07-08): ?selectPid=
     on the URL, forwarded by product-grid.js's group-card link builders from
     the server's g.matched_pid (the filter-matching member under active
     facet/vehicle narrowing). Read once at boot from location.search — never
     re-read on a keepSelection refetch (initial load only, per spec).
     Validated against a plain numeric-id shape; anything else is treated as
     absent (fail open to the pre-existing pid/primary/maybeAutoSelect chain). */
  function parseSelectPid() {
    var raw;
    try {
      raw = new URLSearchParams(window.location.search).get('selectPid');
    } catch (e) {
      return null;
    }
    if (!raw || !/^\d{5,20}$/.test(raw)) return null;
    return parseInt(raw, 10);
  }

  var cfg = {
    pid: parseInt(root.getAttribute('data-product-id'), 10),
    vid: root.getAttribute('data-variant-id') || '',
    handle: root.getAttribute('data-handle') || '',
    priceCents: parseInt(root.getAttribute('data-price-cents'), 10) || 0,
    sectionId: root.getAttribute('data-section-id') || '',
    /* Sibling (per-size) pages run the SAME card locked to the page product:
       no front listbox (a static size row instead), no URL rewriting, no
       auto-select, staggered candidates scoped by siblingScope(). */
    sibling: root.getAttribute('data-mode') === 'sibling',
    selectPid: parseSelectPid()
  };
  if (!cfg.pid) return;

  var state = {
    group: null,
    members: [],
    sel: null,
    rearSel: null,
    staggered: false,
    rearOpen: false,
    /* Sibling page-as-rear mode (owner 2026-08-20): true when the locked
       page product has NO rear candidates (widest width at its diameter/
       pattern) but narrower fronts exist — the rear* slot fields above
       then sell the FRONT axle and the page product is the REAR. Set by
       renderCard via siblingStaggerPlan; always false on hub pages. */
    pageAsRear: false,
    vehicleId: null,
    showAll: false,
    diaScope: null,
    giant: false
  };

  /* Card DOM refs (populated by buildCard once, after the first good fetch). */
  var built = false;
  var ui = {};

  /* undefined = nothing snapshotted yet (see applySelection). */
  var embedResultSnapshot;

  /* Params forwarded onto same-page navigation links (finishes rail, the
     sibling "see all options" link) so a shopper mid package-flow doesn't
     lose context clicking around a PDP — same param set product-grid.js's
     pushVehicleParams uses, plus the two package-mode slot params. Reads
     the LIVE location.search each call (never cached) since pushState
     elsewhere in this file can change it after boot. No-op (returns href
     unchanged) when none of these params are present — the default,
     no-package-params PDP never touches this path's output. */
  var PKG_CONTEXT_PARAMS = ['pkgW', 'pkgT', 'pkgWheel', 'tireDiameter',
    'diameter', 'fit', 'fromPackages', 'wheelDia', 'includeMods',
    'vehicleId', 'year', 'make', 'model', 'submodel'];

  function urlWithPackageContext(href) {
    if (!href) return href;
    var src;
    try {
      src = new URLSearchParams(window.location.search);
    } catch (e) {
      return href; /* ancient browser — leave links exactly as rendered */
    }
    var qsParts = [];
    for (var i = 0; i < PKG_CONTEXT_PARAMS.length; i++) {
      var k = PKG_CONTEXT_PARAMS[i];
      var v = src.get(k);
      if (v) qsParts.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
    }
    if (!qsParts.length) return href;
    var sep = href.indexOf('?') === -1 ? '?' : '&';
    return href + sep + qsParts.join('&');
  }

  function money(cents) {
    if (typeof formatMoney === 'function') return formatMoney(cents);
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Range display ($649 – $1,174): whole dollars, comma separators, decimals
     only when non-integral (theme formatMoney has no thousands grouping). */
  function moneyRange(cents) {
    var d = cents / 100;
    var s = (Math.round(d) === d) ? String(Math.round(d)) : d.toFixed(2);
    return '$' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function toCents(dollars) {
    return Math.round(Number(dollars) * 100);
  }

  function cartApi() {
    /* CartAPI is a top-level const in global.js — script-scope global, NOT a
       window property. Bare-name lookup with a typeof guard. */
    if (typeof CartAPI !== 'undefined' && CartAPI && typeof CartAPI.add === 'function') return CartAPI;
    if (window.CartAPI && typeof window.CartAPI.add === 'function') return window.CartAPI;
    return null;
  }

  function whenFitmentReady(cb) {
    if (window.TFSFitment) { cb(); return; }
    var fired = false;
    function fire() {
      if (fired) return;
      fired = true;
      cb();
    }
    document.addEventListener('tfs-fitment-ready', fire, { once: true });
    setTimeout(fire, FITMENT_READY_TIMEOUT_MS);
  }

  function resolveVehicleId(cb) {
    var F = window.TFSFitment;
    if (!F || typeof F.getVehicle !== 'function') { cb(null); return; }
    var vehicle = F.getVehicle();
    if (!vehicle) { cb(null); return; }
    if (typeof F.resolveVehicleId !== 'function') {
      cb(vehicle.id || vehicle.vehicleId || null);
      return;
    }
    F.resolveVehicleId(vehicle).then(function (resolved) {
      cb(resolved ? (resolved.id || resolved.vehicleId || null) : null);
    }).catch(function () {
      cb(null);
    });
  }

  /* --- Fetch (TFSFitment.api first, first-party app-proxy fallback) --- */

  function fetchViaProxy(qs) {
    return fetch('/apps/fitment/group?' + qs, {
      method: 'GET',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('group proxy fetch failed (' + res.status + ')');
      return res.json();
    });
  }

  /* pkgW/pkgT raw pass-through (Stage 3 Task 1): the server only engages
     its post-cache annotation enricher when one of these is present
     (fitmentPublic.js groupPkgEnricher) — the /group cache KEY is
     unchanged either way, so forwarding these here has zero effect on
     cache behavior for the no-package-params path (both params absent
     → empty string → today's querystring byte-for-byte). */
  function pkgQueryContext() {
    var qs = '';
    try {
      var params = new URLSearchParams(window.location.search);
      var pkgW = params.get('pkgW');
      var pkgT = params.get('pkgT');
      if (pkgW) qs += '&pkgW=' + encodeURIComponent(pkgW);
      if (pkgT) qs += '&pkgT=' + encodeURIComponent(pkgT);
    } catch (e) { /* ancient browser — no package context, unchanged fetch */ }
    return qs;
  }

  function fetchGroup(vehicleId) {
    var qs = 'productId=' + encodeURIComponent(cfg.pid) +
      (vehicleId ? '&vehicleId=' + encodeURIComponent(vehicleId) : '') +
      pkgQueryContext();
    var primary = null;
    var F = window.TFSFitment;
    try {
      if (F && typeof F.api === 'function') {
        primary = F.api('/group?' + qs);
      } else if (F && F.api && typeof F.api.get === 'function') {
        primary = F.api.get('/group?' + qs);
      }
    } catch (e) {
      primary = null;
    }
    if (!primary) return fetchViaProxy(qs);
    return primary.catch(function () {
      return fetchViaProxy(qs);
    });
  }

  /* --- Type context (payload-driven; falls back to member shape) --- */

  function groupIsTires() {
    var g = state.group;
    if (!g) return false;
    var t = g.productType != null ? String(g.productType) : '';
    if (t) return t.toLowerCase().indexOf('tire') !== -1;
    for (var i = 0; i < state.members.length; i++) {
      if (tireSizeOf(state.members[i])) return true;
    }
    return false;
  }

  function unitNoun() {
    return groupIsTires() ? 'tire' : 'wheel';
  }

  /* --- Labels --- */

  function offsetLabel(o) {
    if (o == null) return '—';
    return (o >= 0 ? '+' + o : String(o)) + 'mm';
  }

  /* vt (owner bug report 2026-07-08, duplicate-size differentiation) —
     short display-safe tag (e.g. "SL", "LR E") the server derives ONLY when
     two members of this group share an identical size string
     (productGroupService.js applyVariantTags). Appended to every
     rowSizeText call site (listbox rows, toggle text, sibling fixed row,
     pkg-annotations m.size) so duplicate-size rows stay distinguishable
     even when li/lr already tie. Absent (undefined) for the common
     non-duplicate case — text is byte-identical to before then. */
  function rowSizeText(m) {
    var ts = tireSizeOf(m);
    var base = ts || ((m.d != null ? m.d : '?') + 'x' + (m.w != null ? m.w : '?'));
    if (m && m.vt != null && String(m.vt) !== '') return base + ' ' + String(m.vt);
    return base;
  }

  /* Sub-line — wheels: `5X112 | +42MM` (bp verbatim; `CUSTOM | +42MM` for
     cd=1). Tires: `Load 121 · Range E` from li/lr, whichever the payload
     carries ('' until the backend ships them — the sub-line just stays
     empty). Uppercasing is CSS (text-transform). */
  function rowSubText(m) {
    if (tireSizeOf(m)) {
      var bits = [];
      if (m.li != null && String(m.li) !== '') bits.push('Load ' + m.li);
      if (m.lr != null && String(m.lr) !== '') bits.push('Range ' + m.lr);
      return bits.join(' · ');
    }
    var lead = m.cd ? 'Custom' : ((m.bp != null && String(m.bp) !== '') ? String(m.bp) : '—');
    return lead + ' | ' + offsetLabel(m.o);
  }

  /* Availability — never expose raw stock counts (owner 2026-07-02). Sets
     are 4: qty 9+ = plain Available, exactly 8 = two sets, 4-7 = one set,
     1-3 = actual scarcity count. Untracked/MTO items with no stock stay
     buyable and read Preorder. */
  function availInfo(m) {
    var qty = Number(m.qty) || 0;
    if (qty > 0) {
      if (qty <= 3) return { ok: true, low: true, text: 'Only ' + qty + ' left' };
      if (qty <= 7) return { ok: true, low: true, text: '1 set left' };
      if (qty === 8) return { ok: true, low: true, text: '2 sets left' };
      return { ok: true, text: '✓ Available' };
    }
    if (m.ti === 0) return { ok: true, text: 'Preorder' };
    return { ok: false, text: 'Sold out' };
  }

  /* --- Package-flow size scoping (owner spec 2026-08-18) ---

     When a package draft rides the URL (pkgW on a tire PDP / pkgT on a
     wheel PDP), the size pickers answer ONE question: sizes at the chosen
     complement's diameter that fit the vehicle at the flow's fitment scope.
       dia — the locked diameter (?tireDiameter= for tires, ?diameter= for
             wheels — both forwarded by the grid's in-flow card links and
             the wheel PDP's Add Tires → handoff)
       fit — 'g' (guaranteed fits only) | 'all' (guaranteed + aggressive);
             absent = 'all' (PDP-started flows show both — the fitment
             widget does the telling)
     Tire fit tiers come from /vehicle/:id/specs (raw factory | guaranteed
     | aggressive per size — the /group payload collapses them into
     aftermarket_fit), fetched once per page when needed; until the specs
     arrive (or on failure) tier scoping fails open to diameter-only.
     The diameter lock deliberately holds even under the fitment widget's
     showAll escape hatch — "still hide the other diameters". */
  var pkgScope = (function () {
    try {
      var p = new URLSearchParams(window.location.search);
      var hasW = !!p.get('pkgW');
      var hasT = !!p.get('pkgT');
      if (!hasW && !hasT) return null;
      return {
        draftW: hasW,
        draftT: hasT,
        tireDia: p.get('tireDiameter') || '',
        wheelDia: p.get('diameter') || '',
        fit: (p.get('fit') === 'g') ? 'g' : 'all'
      };
    } catch (e) { return null; }
  })();
  var pkgTireTiers = null;       /* {NORMALIZED_SIZE: 'factory'|'guaranteed'|'aggressive'} */
  var pkgTireTiersLoaded = false;

  /* Package-flow ENTRY flag (?fromPackages=true, stamped by the
     /collections/packages grid): drives the Buy Wheels Only / Add Tires →
     button treatment. Module-scoped because syncAddTires and the flow-end
     listener need it, not just buildCard. Cleared by tfs:pkg-flow-end
     (dispatched by cart-interceptor after a Buy Wheels Only add scrubs the
     URL) so the buttons revert to normal PDP behavior in place. */
  var pkgFlowEntry = (function () {
    try {
      return new URLSearchParams(window.location.search).get('fromPackages') === 'true';
    } catch (e) { return false; }
  })();

  function pkgSizeKey(ts) {
    return String(ts).toUpperCase().replace(/\s+/g, '');
  }

  function pkgScopeAllows(m) {
    if (!pkgScope || !m) return true;
    var ts = tireSizeOf(m);
    if (ts) {
      /* Tire member — scoped only when a WHEEL draft anchors the flow. */
      if (!pkgScope.draftW) return true;
      if (pkgScope.tireDia) {
        var rim = tireRimOf(ts);
        if (!rim || !facetValueMatches(pkgScope.tireDia, rim)) return false;
      }
      if (pkgTireTiersLoaded && pkgTireTiers) {
        var tier = pkgTireTiers[pkgSizeKey(ts)];
        if (!tier) return false; /* not listed for this vehicle */
        if (pkgScope.fit === 'g' && tier === 'aggressive') return false;
      }
      return true;
    }
    /* Wheel member — scoped only when a TIRE draft anchors the flow. */
    if (!pkgScope.draftT) return true;
    if (pkgScope.wheelDia && m.d != null) {
      if (!facetValueMatches(pkgScope.wheelDia, m.d)) return false;
    }
    return true;
  }

  /* Tier rank for row ordering + amber tagging (owner 2026-08-18 r3):
     guaranteed fits (factory|guaranteed) first, aggressive ("mods may be
     required") below, unlisted last (visible outside the flow only). */
  function pkgTierRank(m) {
    var ts = tireSizeOf(m);
    if (!ts || !pkgTireTiersLoaded || !pkgTireTiers) return 0;
    var t = pkgTireTiers[pkgSizeKey(ts)];
    if (t === 'factory' || t === 'guaranteed') return 0;
    if (t === 'aggressive') return 1;
    return 2;
  }

  function pkgTierIsUnlisted(m) {
    return pkgTierRank(m) === 2;
  }

  /* One-shot tier fetch; on arrival re-render rows and rescue a selection
     the scope just hid (same idiom as the tfs:fitment-filter handler).
     r3: loads on EVERY tire PDP with a vehicle (not just package flow) —
     the tiers drive guaranteed-first row ordering and the amber
     "Mods may be required" row tags sitewide. */
  function loadPkgTireTiers() {
    if (pkgTireTiersLoaded) return;
    if (!state.vehicleId || !groupIsTires()) return;
    var F = window.TFSFitment;
    if (!F || typeof F.getVehicleSpecs !== 'function') { pkgTireTiersLoaded = true; return; }
    F.getVehicleSpecs(state.vehicleId).then(function (specs) {
      var map = {};
      ((specs && specs.tireSizes) || []).forEach(function (row) {
        if (row && row.tireSize) map[pkgSizeKey(row.tireSize)] = row.fitType || '';
      });
      pkgTireTiers = map;
      pkgTireTiersLoaded = true;
      if (!built) return;
      var auto = maybeAutoSelect(state.sel);
      if (auto) applySelection(auto, {});
      else renderCard();
      if (state.group) dispatchPkgAnnotations(state.group, true);
    }).catch(function () {
      pkgTireTiersLoaded = true; /* fail open: diameter-only scoping */
    });
  }

  /* --- Wheel-side draft mode (owner 2026-08-18 r2) ---

     A wheel PDP reached mid-flow WITH a tire draft on the URL (?pkgT= — the
     builder's "Swap wheels" hop) must complete the round trip: the ATC
     becomes "Review Package", and submitting merges the selected wheel into
     the draft and returns to the builder. The tire-side twin lives in
     pdp-package-price.liquid (goDraftReview); wheels have no such snippet,
     so the picker owns it. */

  function wheelDraftMode() {
    return !!(pkgScope && pkgScope.draftT) && !groupIsTires();
  }

  /* Minimal wheel-side mirror of slot-parser's mergeSlotParam (slot-parser
     is not loaded on PDPs). cart: tokens dropped — the builder re-reads the
     live cart on every quote. */
  function mergeWheelSlot(rawW, memberStr, targetTag) {
    var out = { f: null, r: null };
    var untagged = 0;
    String(rawW || '').split(',').forEach(function (tok) {
      if (!tok) return;
      var m = /^(f|r|cart):(.+)$/.exec(tok);
      if (m) {
        if ((m[1] === 'f' || m[1] === 'r') && !out[m[1]]) out[m[1]] = m[2];
        return;
      }
      if (untagged === 0 && !out.f) out.f = tok;
      else if (!out.r) out.r = tok;
      untagged++;
    });
    out[targetTag === 'r' ? 'r' : 'f'] = memberStr;
    var members = [];
    if (out.f) members.push(out.f);
    if (out.r) members.push('r:' + out.r);
    return members.join(',');
  }

  function emitPickMember(sel, qty) {
    if (!sel || sel.pid == null) return '';
    var s = String(sel.pid);
    if (sel.vid != null && String(sel.vid) !== '') {
      s += ':' + sel.vid;
      if (qty) s += ':' + qty;
    }
    return s;
  }

  function goWheelDraftReview() {
    var front = state.sel;
    if (!front) return;
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var rawW = params.get('pkgW') || '';
    var rawT = params.get('pkgT') || '';
    var fq = readQty(ui.frontQtyInput, 4) || 4;
    var w;
    if (state.staggered && state.rearSel) {
      var rq = readQty(ui.rearQtyInput, 2) || 2;
      /* Page-as-rear: the slot pick takes the untagged FRONT slot and the
         page product goes to r: — same wire grammar either way. */
      w = state.pageAsRear
        ? emitPickMember(state.rearSel, rq) + ',r:' + emitPickMember(front, fq)
        : emitPickMember(front, fq) + ',r:' + emitPickMember(state.rearSel, rq);
    } else {
      var axle = params.get('axle') === 'r' ? 'r' : 'f';
      w = mergeWheelSlot(rawW, emitPickMember(front, fq), axle);
    }
    if (!w) return;
    var url = '/pages/package-builder?w=' + encodeURIComponent(w);
    if (rawT) url += '&t=' + encodeURIComponent(rawT);
    ['vehicleId', 'year', 'make', 'model', 'submodel', 'fit'].forEach(function (k) {
      var v = params.get(k);
      if (v) url += '&' + k + '=' + encodeURIComponent(v);
    });
    window.location.href = url;
  }

  /* Cancel the package from a wheel PDP (owner r2: "a user should be able
     to cancel package from all sides"): tombstone + stash clears + URL
     scrub, then a plain reload lands on the regular PDP. Tire PDPs get the
     same affordance from pdp-package-price.liquid instead. */
  function cancelPkgFromPicker() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { params = null; }
    if (params && (params.get('pkgW') || params.get('pkgT') || params.get('pkgWheel'))) {
      try {
        sessionStorage.setItem('tfs_pkg_cancelled', JSON.stringify({
          w: params.get('pkgW') || '',
          t: params.get('pkgT') || '',
          pw: params.get('pkgWheel') || ''
        }));
      } catch (e) { /* ignore */ }
    }
    try {
      sessionStorage.removeItem('tfs_pkg_wheel');
      sessionStorage.removeItem('tfs_pkg_tire');
      sessionStorage.removeItem('tfs_cross_sell_diameter');
    } catch (e) { /* ignore */ }
    try {
      var scrub = new URLSearchParams(window.location.search);
      ['pkgW', 'pkgT', 'pkgWheel', 'pkgReview', 'fromPackages', 'fromPackagesTire',
        'wheelDia', 'chosenWheel', 'includeMods', 'fit', 'axle', 'slot', 'tireDiameter'
      ].forEach(function (k) { scrub.delete(k); });
      var qs = scrub.toString();
      history.replaceState(history.state, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    } catch (e) { /* ignore */ }
    window.location.reload();
  }

  /* --- Members visible under the current mode --- */

  function visibleMembers() {
    var list = state.members;
    if (cfg.sibling) list = siblingScope(list, findMemberByPid(cfg.pid));
    if (state.giant && !state.vehicleId) {
      if (state.diaScope == null) return [];
      list = list.filter(function (m) { return num(m.d) === num(state.diaScope); });
    }
    if (state.vehicleId) list = filterByFit(list, state.showAll);
    if (pkgScope) list = list.filter(pkgScopeAllows);
    // Owner 2026-08-18 r2: sold-out TIRE sizes are hidden everywhere — plain
    // PDPs and package flow alike. Wheels keep their sold-out rows (they
    // stay pickable for the back-in-stock flow).
    list = list.filter(function (m) { return !tireSizeOf(m) || memberAvailable(m); });
    return list;
  }

  function indexOfPid(list, pid) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].pid === pid) return i;
    }
    return -1;
  }

  function findMemberByPid(pid) {
    var n = parseInt(pid, 10);
    var i = indexOfPid(state.members, n);
    return i === -1 ? null : state.members[i];
  }

  function findMemberByHandle(handle) {
    for (var i = 0; i < state.members.length; i++) {
      if (state.members[i].handle === handle) return state.members[i];
    }
    return null;
  }

  /* ---------------------------------------------------------------
     Filter-context initial selection (owner bug report 2026-07-08)

     Grid click-through must land on a member that actually matches what the
     shopper had filtered to on the grid — not the group's default/primary
     pick. Two independent signals can carry that context onto the PDP URL:

       (a) ?selectPid — the server-resolved exact match (typesenseSearch.js
           buildGroupCard's g.matched_pid), forwarded verbatim by
           product-grid.js's group-card link builders. This is the live,
           shipped mechanism.
       (b) Facet params (?diameter=/?width=/?tireDiameter=) — the SAME
           param names product-grid.js's FILTER_PARAMS/readURLState uses on
           the grid page. The grid does NOT currently forward these onto
           its own card links (only vehicle params + selectPid), so this
           path is a defensive/forward-compatible fallback for any other
           entry point that lands on this PDP with those params already on
           the URL (a shared/bookmarked link, a future grid change) —
           never required for the shipped click-through fix, always safe to
           have.

     Both are INITIAL-LOAD ONLY (never on a keepSelection vehicle refetch —
     the shopper's live in-page pick must never be silently overridden by
     stale URL context from the page's first load). Fit still wins: a
     selectPid/facet match that does_not_fit the active vehicle is rejected
     and the caller falls through to the pre-existing pid/primary chain. */

  function parseFacetParams() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return null;
    }
    var out = {};
    var any = false;
    ['diameter', 'width', 'tireDiameter'].forEach(function (k) {
      var v = params.get(k);
      if (v != null && v !== '') { out[k] = v; any = true; }
    });
    return any ? out : null;
  }

  /* Numeric-CSV membership test — facet params can carry a single value
     ("18") or a comma-joined multi-select ("18,19"), mirroring
     product-grid.js's own filter value shape. */
  function facetValueMatches(paramVal, memberNum) {
    if (memberNum == null) return false;
    var parts = String(paramVal).split(',');
    for (var i = 0; i < parts.length; i++) {
      var n = Number(String(parts[i]).trim());
      if (!isNaN(n) && n === Number(memberNum)) return true;
    }
    return false;
  }

  /* Members matching the parsed facet params — wheels compare diameter/
     width off m.d/m.w directly; tires compare tireDiameter off the parsed
     rim (tireRimOf/tireSizeOf) — there is no member-level "width" facet for
     tires (mirrors memberWidth's null-for-tires convention elsewhere in
     this file). A member must satisfy every facet key present in the
     params (AND across keys, same as the grid's own multi-facet narrowing). */
  function membersMatchingFacets(list, facets) {
    if (!facets) return [];
    return (list || []).filter(function (m) {
      if (!m) return false;
      var ts = tireSizeOf(m);
      if (facets.diameter != null) {
        if (ts) return false; // a diameter (wheel) facet can't match a tire member
        if (!facetValueMatches(facets.diameter, m.d)) return false;
      }
      if (facets.width != null) {
        if (ts) return false;
        if (!facetValueMatches(facets.width, m.w)) return false;
      }
      if (facets.tireDiameter != null) {
        if (!ts) return false; // a tireDiameter facet can't match a wheel member
        var rim = tireRimOf(ts);
        if (!rim || !facetValueMatches(facets.tireDiameter, rim)) return false;
      }
      return true;
    });
  }

  /* Resolves the filter-context initial pick per the (a)/(b) precedence
     above. Returns a member or null (caller falls through to the existing
     pid/primary chain on null). `vehicleOn` gates the fit check — with no
     active vehicle every candidate passes (nothing to filter against). */
  function resolveFilterContextSelection(vehicleOn) {
    if (cfg.selectPid != null) {
      var bypid = findMemberByPid(cfg.selectPid);
      if (bypid) {
        var fitsA = !vehicleOn || filterByFit([bypid], false).length > 0;
        if (fitsA) return bypid;
        /* does_not_fit — ignore and fall through to facet match / default. */
      }
    }
    var facets = parseFacetParams();
    if (facets) {
      var candidates = membersMatchingFacets(state.members, facets);
      if (vehicleOn) candidates = filterByFit(candidates, false);
      if (candidates.length) return bestFit(candidates, null);
    }
    return null;
  }

  /* ---------------------------------------------------------------
     DOM helpers
     --------------------------------------------------------------- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* Hardened text/attribute escaper (Stage 3 Task 1; provenance: same
     class as package-builder.js's esc — quote-escaping on top of a
     textContent pass covers both plain-text and attribute-interpolation
     use). Everything in THIS file already renders via el()/textContent,
     which is inherently safe, but the new pkg-annotation copy stitches
     numeric dims into strings before handing them to el() — esc() keeps
     that path defensively consistent (and covers any future attribute
     use) without changing any existing rendering. */
  function esc(s) {
    var d = document.createElement('div');
    d.appendChild(document.createTextNode(s == null ? '' : String(s)));
    return d.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function readQty(input, fallback) {
    if (!input) return fallback;
    var v = parseInt(input.value, 10);
    return isNaN(v) ? fallback : v;
  }

  function setQtyInput(input, v) {
    if (!input) return;
    input.value = String(v);
    try { input.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) { /* old WebView */ }
  }

  /* Adopting .pdp-quantity-row re-connects the <quantity-input> custom
     element, whose connectedCallback re-binds +/− click handlers → double
     stepping. This capture handler swallows the button clicks and applies
     exactly one step itself (change event still fires on the ORIGINAL input,
     so product-form.js's qty sync keeps working). */
  function hijackQtyButtons(row, getInput) {
    row.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var btn = t.closest('button[name="minus"], button[name="plus"]');
      if (!btn || !row.contains(btn)) return;
      e.stopPropagation();
      e.preventDefault();
      stepQty(getInput(), btn.getAttribute('name') === 'plus' ? 1 : -1);
    }, true);
  }

  function stepQty(input, delta) {
    if (!input) return;
    var min = parseInt(input.min, 10);
    if (isNaN(min)) min = 1;
    var max = parseInt(input.max, 10);
    if (isNaN(max) || !max) max = 999;
    var v = parseInt(input.value, 10);
    if (isNaN(v)) v = min;
    v += delta;
    if (v < min) v = min;
    if (v > max) v = max;
    input.value = String(v);
    try { input.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) { /* non-fatal */ }
  }

  /* ---------------------------------------------------------------
     Custom listbox (rich rows — size/bp/offset left, price/availability right)
     --------------------------------------------------------------- */

  var listboxes = [];

  function createListbox(axle, placeholder) {
    var lb = {
      axle: axle,
      placeholder: placeholder,
      rows: [],
      selPid: null,
      open: false,
      active: -1,
      onPick: null
    };
    lb.wrap = el('div', 'pdp-gp-listbox');
    lb.wrap.setAttribute('data-gp-listbox', axle);

    lb.toggle = el('button', 'pdp-gp-toggle');
    lb.toggle.type = 'button';
    lb.toggle.setAttribute('aria-haspopup', 'listbox');
    lb.toggle.setAttribute('aria-expanded', 'false');
    var text = el('span', 'pdp-gp-toggle-text');
    lb.toggleMain = el('span', 'pdp-gp-toggle-size');
    lb.toggleSub = el('span', 'pdp-gp-toggle-sub');
    text.appendChild(lb.toggleMain);
    text.appendChild(lb.toggleSub);
    lb.toggle.appendChild(text);
    lb.toggle.appendChild(el('span', 'pdp-gp-chevron'));

    lb.panel = el('ul', 'pdp-gp-panel');
    lb.panel.id = 'pdp-gp-panel-' + axle + '-' + cfg.sectionId;
    lb.panel.setAttribute('role', 'listbox');
    lb.panel.hidden = true;
    lb.toggle.setAttribute('aria-controls', lb.panel.id);

    lb.wrap.appendChild(lb.toggle);
    lb.wrap.appendChild(lb.panel);

    lb.toggle.addEventListener('click', function () {
      if (lb.open) closePanel(lb);
      else openPanel(lb);
    });
    lb.toggle.addEventListener('keydown', function (e) { onListboxKey(lb, e); });

    listboxes.push(lb);
    return lb;
  }

  /* Row annotation copy from a member's pkg.front/rear entry (Stage 3
     Task 1, spec §6.2 reason enum: ok/no_deal/deal_expired/data_gap/
     rim_mismatch/width_mismatch). v1 (front-axle only — see setListRows):
     the FRONT annotation is what drives every row here, listbox rows are
     never rear-scoped independent of the front pick. Returns null (render
     nothing) for no_deal/data_gap/deal_expired — NEVER speculative "no
     deal" copy, per spec: those reasons mean "can't tell/not live", not
     "this size definitely has no deal". {dia}/{w} come from the row
     member's OWN dims (memberDia/memberWidth) — the only per-axle
     dimensions the /group payload actually carries client-side (the
     wheel-anchor's own dims are server-internal, stripped before the
     response ever reaches the browser — stripPackageInternals). For a
     wheel row (tireAxles branch) that's the real wheel dia×width; for a
     tire row (the common pkgW flow) width is unavailable so it's dropped
     from the phrase rather than fabricated. */
  function pkgAnnotationText(m) {
    var pkg = m && m.pkg;
    var entry = pkg && pkg.front;
    if (!entry) return null;
    if (entry.eligible && entry.discountSet > 0) {
      // Owner 2026-08-18 r2: never surface the per-size package discount in
      // the picker rows ("−$X/set") — the logic stays, the money doesn't
      // show until cart/checkout. Fitment mismatch phrasing below is kept
      // (that's fitment info, not deal talk).
      return null;
    }
    if (entry.reason === 'rim_mismatch' || entry.reason === 'width_mismatch') {
      var dia = memberDia(m);
      var width = memberWidth(m);
      if (dia != null && width != null) {
        return 'doesn’t fit your ' + esc(dia) + '×' + esc(width) + ' wheels';
      }
      if (dia != null) {
        return 'doesn’t fit your ' + esc(dia) + '" wheels';
      }
      return 'doesn’t pair with your wheels';
    }
    /* no_deal / data_gap / deal_expired — cannot-annotate, not "no deal". */
    return null;
  }

  function setListRows(lb, rows, sel) {
    lb.rows = rows;
    lb.selPid = sel ? sel.pid : null;
    lb.active = -1;
    while (lb.panel.firstChild) lb.panel.removeChild(lb.panel.firstChild);

    rows.forEach(function (m, idx) {
      var li = el('li', 'pdp-gp-option');
      li.id = lb.panel.id + '-opt-' + idx;
      li.setAttribute('role', 'option');
      var isSel = !!sel && m.pid === sel.pid;
      li.setAttribute('aria-selected', isSel ? 'true' : 'false');
      if (isSel) li.className += ' is-selected';
      /* pid-first row matching (owner bug report 2026-07-08) — lets
         pdp-package-price.liquid's overlay chips join a row by product id
         instead of the fragile size-text match (which collides on true
         duplicate sizes). setAttribute is already injection-safe (not
         string-concatenated HTML); esc()'d anyway per file convention —
         pids are always numeric but this stays consistent with every other
         attribute-interpolation site in this file. */
      if (m && m.pid != null) li.setAttribute('data-pid', esc(String(m.pid)));

      var left = el('span', 'pdp-gp-opt-left');
      var sizeLine = el('span', 'pdp-gp-opt-size', rowSizeText(m));
      if (m.cd) sizeLine.appendChild(el('span', 'pdp-gp-custom-pill', 'Custom'));
      /* Unlisted tire sizes carry the amber alert right on the row — the DB
         tier is the authority. Listed aggressive sizes are Aftermarket Fits
         (owner 2026-09-30), so they no longer get the pill. */
      if (groupIsTires() && state.vehicleId && pkgTierIsUnlisted(m)) {
        sizeLine.appendChild(el('span', 'pdp-gp-mods-pill', '⚠ Mods may be required'));
      }
      left.appendChild(sizeLine);
      left.appendChild(el('span', 'pdp-gp-opt-sub', rowSubText(m)));

      /* Package-deal annotation (Stage 3 Task 1): FRONT axle only — a
         staggered rear pick's own annotation belongs to the rear picker
         flow (kept out of v1 scope per plan; the rear list here still
         shows plain rows, unannotated, same as before this change). Only
         ever populated when the /group fetch carried pkgW/pkgT (server
         only annotates in that case — m.pkg.front is otherwise the
         pre-existing {ss} shape with no .front key, so pkgAnnotationText
         returns null and nothing renders on the no-params path). */
      if (lb.axle === 'front') {
        var annoText = pkgAnnotationText(m);
        if (annoText) left.appendChild(el('span', 'pdp-gp-opt-pkg', annoText));
      }

      var right = el('span', 'pdp-gp-opt-right');
      right.appendChild(el('span', 'pdp-gp-opt-price', money(toCents(m.price))));
      var av = availInfo(m);
      right.appendChild(el('span', 'pdp-gp-opt-avail' + (av.low ? ' is-low' : av.ok ? ' is-ok' : ' is-out'), av.text));

      li.appendChild(left);
      li.appendChild(right);

      /* Sold-out rows stay selectable — back-in-stock flow. */
      li.addEventListener('click', function () {
        closePanel(lb);
        if (lb.onPick) lb.onPick(m);
      });
      li.addEventListener('mouseenter', function () { setActive(lb, idx, true); });

      lb.panel.appendChild(li);
    });

    lb.toggle.disabled = rows.length === 0;
    if (lb.open && rows.length === 0) closePanel(lb);
  }

  function renderToggle(lb, sel) {
    if (sel) {
      lb.toggleMain.textContent = rowSizeText(sel);
      lb.toggleSub.textContent = rowSubText(sel);
    } else {
      lb.toggleMain.textContent = lb.placeholder;
      lb.toggleSub.textContent = '';
    }
  }

  /* The absolute panel must escape ancestor overflow clipping while open:
     the REAR list sits at the card's bottom edge, so its 340px panel
     otherwise gets swallowed by .pdp-gp-card's rounded-corner
     overflow:hidden (and .pdp-info's) — unreachable rows below the card
     boundary. State class added on open, removed on close; CSS flips those
     ancestors to overflow:visible only for that window. */
  function setClipEscape(lb, on) {
    if (!lb.wrap.closest) return; /* ancient browsers keep the old clipping */
    var sels = ['.pdp-gp-card', '.pdp-info'];
    for (var i = 0; i < sels.length; i++) {
      var node = lb.wrap.closest(sels[i]);
      if (node) node.classList.toggle('pdp-gp-lb-open', on);
    }
  }

  function openPanel(lb) {
    for (var i = 0; i < listboxes.length; i++) {
      if (listboxes[i] !== lb) closePanel(listboxes[i]);
    }
    if (lb.open || lb.toggle.disabled || !lb.rows.length) return;
    lb.open = true;
    lb.panel.hidden = false;
    lb.wrap.classList.add('is-open');
    setClipEscape(lb, true);
    lb.toggle.setAttribute('aria-expanded', 'true');
    var idx = 0;
    if (lb.selPid != null) {
      var selIdx = indexOfPid(lb.rows, lb.selPid);
      if (selIdx !== -1) idx = selIdx;
    }
    setActive(lb, idx, false);
  }

  function closePanel(lb) {
    if (!lb.open) return;
    lb.open = false;
    lb.panel.hidden = true;
    lb.wrap.classList.remove('is-open');
    setClipEscape(lb, false);
    lb.toggle.setAttribute('aria-expanded', 'false');
    lb.toggle.removeAttribute('aria-activedescendant');
    lb.active = -1;
  }

  function setActive(lb, idx, fromHover) {
    if (!lb.rows.length) return;
    if (idx < 0) idx = 0;
    if (idx > lb.rows.length - 1) idx = lb.rows.length - 1;
    var opts = lb.panel.children;
    if (lb.active >= 0 && opts[lb.active]) opts[lb.active].classList.remove('is-active');
    lb.active = idx;
    var node = opts[idx];
    if (node) {
      node.classList.add('is-active');
      lb.toggle.setAttribute('aria-activedescendant', node.id);
      if (!fromHover && node.scrollIntoView) {
        try { node.scrollIntoView({ block: 'nearest' }); } catch (e) { /* older Safari */ }
      }
    }
  }

  function onListboxKey(lb, e) {
    var key = e.key;
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      e.preventDefault();
      if (!lb.open) { openPanel(lb); return; }
      setActive(lb, lb.active + (key === 'ArrowDown' ? 1 : -1), false);
    } else if (key === 'Enter' || key === ' ') {
      /* Closed: let the button's default click fire (opens). Open: select. */
      if (!lb.open) return;
      e.preventDefault();
      var m = lb.rows[lb.active];
      closePanel(lb);
      if (m && lb.onPick) lb.onPick(m);
    } else if (key === 'Escape') {
      if (lb.open) {
        e.preventDefault();
        closePanel(lb);
      }
    }
  }

  function onDocClick(e) {
    for (var i = 0; i < listboxes.length; i++) {
      var lb = listboxes[i];
      if (lb.open && !(e.target && lb.wrap.contains(e.target))) closePanel(lb);
    }
  }

  /* ---------------------------------------------------------------
     Card construction — runs ONCE, only after a successful group fetch.
     Adopts (moves, never clones) .pdp-quantity-row, .pdp-pricing and
     .pdp-buy-buttons so every existing behavior rides along.
     --------------------------------------------------------------- */

  function tireIcon() {
    var span = el('span', 'pdp-gp-addtires-icon');
    span.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"></circle><circle cx="12" cy="12" r="3.5"></circle><path d="M12 3v5.5M12 15.5V21M3 12h5.5M15.5 12H21"></path></svg>';
    return span;
  }

  function buildCard() {
    if (built) return true;
    /* root itself carries data-section-id (the snippet's own attribute), so
       closest() from root matches root and scopes every adoptee query to the
       empty picker div. Start from the parent to reach the real section. */
    var section = (root.parentElement && root.parentElement.closest('[data-section-id]')) || document;
    var pricing = document.getElementById('price-' + cfg.sectionId) ||
      section.querySelector('.pdp-pricing');
    var buyButtons = section.querySelector('.pdp-buy-buttons');
    if (!pricing || !buyButtons) return false;
    var qtyRow = section.querySelector('.pdp-quantity-row');
    var crossSell = document.querySelector('.tfs-cross-sell');

    ui.pricing = pricing;
    ui.buyButtons = buyButtons;
    ui.qtyRow = qtyRow || null;
    ui.crossSell = crossSell || null;

    ui.card = el('div', 'pdp-gp-card');

    /* Card header dropped (owner, 2026-07-05): the card now leads with price,
       matching the Buy Box Redesign mockup. */

    /* ---- Price zone: ADOPT the whole .pdp-pricing block. Leads the card
       (owner, 2026-07-03): pricing → size → staggered → qty → buttons →
       buy-foot notes. The staggered set-total block swaps in when a rear
       is engaged. ---- */
    ui.priceZone = el('div', 'pdp-gp-price');
    ui.priceZone.appendChild(pricing);
    ui.stgPrice = el('div', 'pdp-gp-stgprice');
    ui.stgPrice.hidden = true;
    ui.priceZone.appendChild(ui.stgPrice);
    ui.card.appendChild(ui.priceZone);

    /* ---- Front (or only) selection block ---- */
    ui.frontBlock = el('div', 'pdp-gp-block');
    var head = el('div', 'pdp-gp-head');
    ui.frontLabel = el('span', 'pdp-gp-heading', 'Your Size');
    head.appendChild(ui.frontLabel);
    if (!cfg.sibling) {
      ui.frontRemove = el('button', 'pdp-gp-remove', 'Remove');
      ui.frontRemove.type = 'button';
      ui.frontRemove.addEventListener('click', clearFront);
      head.appendChild(ui.frontRemove);
    }
    ui.frontBlock.appendChild(head);

    if (cfg.sibling) {
      /* Sibling pages sell ONE size — a locked row (same look as a selected
         toggle, no chevron/interaction) instead of the front listbox. */
      ui.fixedRow = el('div', 'pdp-gp-fixed');
      ui.frontBlock.appendChild(ui.fixedRow);
    } else {
      /* Giant-group diameter chips (shown instead of the listbox until a
         diameter is scoped or a vehicle is set). */
      ui.giantBox = el('div', 'pdp-group-picker__prompt');
      ui.giantBox.hidden = true;
      ui.frontBlock.appendChild(ui.giantBox);

      ui.frontList = createListbox('front', groupIsTires() ? 'Choose your size' : 'Choose size & offset');
      ui.frontList.onPick = function (m) { applySelection(m, {}); };
      ui.frontBlock.appendChild(ui.frontList.wrap);
    }

    /* Per-selection meta row under the toggle: PART # (click-to-copy) · BORE.
       Refreshed by renderMeta() on every selection (sku + hb ride in the
       /group member payload). */
    ui.metaRow = el('div', 'pdp-gp-meta');
    ui.metaRow.hidden = true;
    ui.metaSku = el('button', 'pdp-gp-meta-sku');
    ui.metaSku.type = 'button';
    ui.metaSku.title = 'Copy part number';
    ui.metaSku.addEventListener('click', copySelectedSku);
    ui.metaBore = el('span', 'pdp-gp-meta-bore');
    ui.metaRow.appendChild(ui.metaSku);
    ui.metaRow.appendChild(ui.metaBore);
    ui.frontBlock.appendChild(ui.metaRow);

    ui.card.appendChild(ui.frontBlock);

    /* ---- Staggered opener — the rear stays IMPLICIT (owner: less clutter).
       Groups that run staggered show this button; clicking reveals the rear
       block. The rear Remove hides it again and restores a set of 4. ---- */
    ui.stgBtn = el('button', 'pdp-gp-stg-open');
    ui.stgBtn.type = 'button';
    ui.stgBtn.hidden = true;
    ui.stgBtn.innerHTML =
      '<span class="pdp-gp-stg-open__plus" aria-hidden="true">+</span>' +
      '<span>Run It Staggered &mdash; Add Rear ' + (groupIsTires() ? 'Tires' : 'Wheels') + '</span>';
    /* Page-as-rear relabeling target (renderCard): the axle word flips to
       "Front" when the sibling slot sells fronts. */
    ui.stgBtnLabel = ui.stgBtn.lastChild;
    ui.stgBtn.addEventListener('click', function () {
      state.rearOpen = true;
      renderCard();
    });
    /* Staggered opener lives INSIDE the size block (owner, 2026-07-05): it reads
       as part of "Your Size", not its own divided card section — so it keeps its
       own button border/radius instead of the card's section padding+divider. */
    ui.frontBlock.appendChild(ui.stgBtn);

    /* ---- Rear selection block — revealed by the opener above. Muted until
       a rear is picked; picking one engages staggered mode (quantities
       4 → 2+2), its Remove collapses back to the opener (set of 4). ---- */
    ui.rearBlock = el('div', 'pdp-gp-block pdp-gp-block--rear');
    ui.rearBlock.hidden = true;
    var rhead = el('div', 'pdp-gp-head');
    var rlabel = el('span', 'pdp-gp-heading', 'Rear');
    rlabel.appendChild(el('span', 'pdp-gp-heading-opt', '· optional'));
    /* Page-as-rear relabeling target (renderCard): the 'Rear' text node —
       never the whole span, which would wipe the · optional child. */
    ui.rearHeadWord = rlabel.firstChild;
    rhead.appendChild(rlabel);
    ui.rearRemove = el('button', 'pdp-gp-remove', 'Remove');
    ui.rearRemove.type = 'button';
    ui.rearRemove.hidden = true;
    ui.rearRemove.addEventListener('click', disengageRear);
    rhead.appendChild(ui.rearRemove);
    ui.rearBlock.appendChild(rhead);
    ui.stgHint = el('p', 'pdp-gp-stagger-hint',
      'Run it staggered — go wider in the rear.');
    ui.rearBlock.appendChild(ui.stgHint);
    ui.rearList = createListbox('rear', 'Choose rear size');
    ui.rearList.onPick = function (m) { engageRear(m); };
    ui.rearBlock.appendChild(ui.rearList.wrap);
    /* Rear meta row — same PART # (click-to-copy) / BORE treatment as the
       front (owner, 2026-07-31). Refreshed by renderRearMeta(). */
    ui.rearMetaRow = el('div', 'pdp-gp-meta');
    ui.rearMetaRow.hidden = true;
    ui.rearMetaSku = el('button', 'pdp-gp-meta-sku');
    ui.rearMetaSku.type = 'button';
    ui.rearMetaSku.title = 'Copy part number';
    ui.rearMetaSku.addEventListener('click', copyRearSku);
    ui.rearMetaBore = el('span', 'pdp-gp-meta-bore');
    ui.rearMetaRow.appendChild(ui.rearMetaSku);
    ui.rearMetaRow.appendChild(ui.rearMetaBore);
    ui.rearBlock.appendChild(ui.rearMetaRow);
    ui.rearQtyRow = null; /* built lazily on first rear engagement */
    ui.rearQtyInput = null;
    ui.frontBlock.appendChild(ui.rearBlock);

    /* ---- Quantity zone: ADOPT the existing stepper (appendChild moves
       it). Sits between the staggered section and the buttons; on rear
       engagement the cloned rear stepper joins it and the labels flip to
       Front/Rear Quantity (renderCard). ---- */
    ui.qtyZone = el('div', 'pdp-gp-qtyzone');
    if (qtyRow) {
      ui.qtyZone.appendChild(qtyRow);
      hijackQtyButtons(qtyRow, function () { return ui.frontQtyInput; });
    }
    ui.frontQtyLabel = qtyRow ? qtyRow.querySelector('label') : null;
    ui.frontQtyInput = qtyRow ? qtyRow.querySelector('input[name="quantity"]') : null;
    if (ui.frontQtyInput) {
      ui.frontQtyInput.addEventListener('change', function () {
        if (state.staggered) renderStgPrice(null);
      });
    }
    ui.card.appendChild(ui.qtyZone);

    ui.error = el('div', 'pdp-gp-error');
    ui.error.hidden = true;
    ui.error.setAttribute('role', 'alert');
    ui.card.appendChild(ui.error);

    /* ---- Button row: cross-sell proxy ("Add Tires" on wheel pages,
       "Add Wheels" on tire pages) + ADOPTED buy-buttons block ----
       Owner spec 2026-08-18, package flow (?fromPackages=true from the
       /collections/packages grid): the ATC reads "Buy Wheels Only"
       (cart-interceptor relabels it; a native add that ends the flow) and
       comes FIRST, with "Add Tires →" second as the continue affordance —
       the CSS order-swap rides the modifier class below. */
    ui.actions = el('div', 'pdp-gp-actions' + (pkgFlowEntry ? ' pdp-gp-actions--pkgflow' : ''));
    ui.addTires = el('button', 'pdp-gp-addtires');
    ui.addTires.type = 'button';
    ui.addTires.appendChild(tireIcon());
    ui.addTiresDefault = groupIsTires()
      ? 'Add Wheels'
      : (pkgFlowEntry ? 'Add Tires →' : 'Add Tires');
    ui.addTiresLabel = el('span', 'pdp-gp-addtires-label', ui.addTiresDefault);
    ui.addTires.appendChild(ui.addTiresLabel);
    if (!crossSell) ui.addTires.disabled = true;
    ui.addTires.addEventListener('click', function () {
      if (ui.crossSell) ui.crossSell.click();
    });
    ui.actions.appendChild(ui.addTires);

    /* Wheel-side draft intercept (owner r2) — registered BEFORE the
       staggered intercept so it wins the capture order: mid-flow (?pkgT=)
       every submit on a wheel PDP routes to the builder, square or
       staggered, never a native add. */
    buyButtons.addEventListener('submit', function (e) {
      if (!wheelDraftMode()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      goWheelDraftReview();
    }, true);

    /* Staggered ATC intercept: capture on the wrapper runs BEFORE
       product-form's own target-phase submit listener; stopPropagation keeps
       the single-variant path from also firing. */
    buyButtons.addEventListener('submit', function (e) {
      if (!state.staggered) return;
      e.preventDefault();
      e.stopPropagation();
      handleStaggeredAdd();
    }, true);
    ui.actions.appendChild(buyButtons);

    /* Wheel-draft chrome: "Review Package" CTA (icon-preserving relabel,
       same pattern as pdp-package-price setBtnLabel) + hide the Add Tires
       proxy via the modifier class (CSS twin of .pdp-pkg-draft). */
    if (wheelDraftMode()) {
      ui.actions.classList.add('pdp-gp-actions--wheeldraft');
      var draftAtc = buyButtons.querySelector('.pdp-add-cart');
      var draftBt = draftAtc && draftAtc.querySelector('.btn-text');
      if (draftBt && !/Sold Out/i.test(draftBt.textContent)) {
        var draftIcon = draftBt.querySelector('svg');
        draftBt.textContent = ' Review Package';
        if (draftIcon) draftBt.insertBefore(draftIcon, draftBt.firstChild);
      }
    }

    /* Adopt the fitment dropdown into the card, ABOVE the size block (owner,
       2026-07-05): confirm the vehicle first, then pick the size (a confirmed
       fit also filters the size list). The fitment app-block's JS targets
       #tfs-verify-fitment by id, so moving the node keeps it wired. */
    var fitMount = document.getElementById('tfs-verify-fitment');
    if (fitMount && ui.frontBlock) {
      ui.fitZone = el('div', 'pdp-gp-fitzone');
      ui.fitZone.appendChild(fitMount);
      ui.card.insertBefore(ui.fitZone, ui.frontBlock);
    }

    ui.card.appendChild(ui.actions);

    /* Cancel package from the wheel side (owner r2) — under the buttons on
       every in-flow wheel PDP (entry AND swap hops). Tire PDPs get theirs
       from pdp-package-price.liquid. */
    if (!groupIsTires() && (pkgFlowEntry || pkgScope)) {
      ui.pkgCancel = el('button', 'pdp-gp-cancel-pkg', '× Cancel package');
      ui.pkgCancel.type = 'button';
      ui.pkgCancel.addEventListener('click', cancelPkgFromPicker);
      ui.card.appendChild(ui.pkgCancel);
    }

    /* ATC button initial text state (avoid stomping the localized label). */
    var atcBtn = buyButtons.querySelector('.pdp-add-cart');
    ui.lastSoldOut = atcBtn ? !!atcBtn.disabled : false;

    /* Keep the Add Tires proxy in step with the cross-sell button's
       cart-aware mode swaps ("Add to Cart & View Cart"). */
    if (crossSell && typeof MutationObserver !== 'undefined') {
      try {
        new MutationObserver(syncAddTires).observe(crossSell, {
          attributes: true,
          attributeFilter: ['disabled', 'data-mode']
        });
      } catch (e) { /* non-fatal */ }
    }
    syncAddTires();

    while (root.firstChild) root.removeChild(root.firstChild);
    root.appendChild(ui.card);
    root.hidden = false;
    /* Marks the picker live — CSS hides the original cross-sell CTA. */
    root.classList.add('pdp-gp-active');
    /* wt host shell (group-UX rollout 2026-07-06): the wheels-tires template
       wraps its buy box in its own .pdp-gp-card.pdp-buybox shell. Once the
       picker's inner card is live the shell drops its chrome and hides its
       leftover CTA row (CSS on .pdp-gp-hosted) so the page shows ONE card.
       Merged templates have no shell — no-op there. Fail-open never gets
       here, so the server-rendered wt card stays untouched on API failure. */
    var hostShell = root.parentElement && root.parentElement.closest
      ? root.parentElement.closest('.pdp-gp-card.pdp-buybox') : null;
    if (hostShell) hostShell.classList.add('pdp-gp-hosted');

    document.addEventListener('click', onDocClick);

    built = true;
    return true;
  }

  function syncAddTires() {
    if (!ui.addTires) return;
    var cs = ui.crossSell;
    if (!cs) { ui.addTires.disabled = true; return; }
    ui.addTires.disabled = !!cs.disabled;
    var mode = cs.getAttribute('data-mode');
    var fallback = ui.addTiresDefault || 'Add Tires';
    /* Package-flow entry belt: the checkout morph is already suppressed at
       the source (pdp-cross-sell checkCartAndUpdate early-returns on
       fromPackages), but if any other writer flips data-mode the proxy must
       stay "Add Tires →" — a morphed label here routed a native wheel-add
       to /cart and silently killed the flow. */
    ui.addTiresLabel.textContent = (mode === 'checkout' && !pkgFlowEntry)
      ? ((cs.textContent || '').replace(/\s+/g, ' ').trim() || fallback)
      : fallback;
  }

  /* Flow end (Buy Wheels Only add landed; cart-interceptor scrubbed the URL
     and dispatched this): revert the button row to normal PDP behavior. */
  document.addEventListener('tfs:pkg-flow-end', function () {
    pkgFlowEntry = false;
    if (ui.actions) ui.actions.classList.remove('pdp-gp-actions--pkgflow');
    ui.addTiresDefault = groupIsTires() ? 'Add Wheels' : 'Add Tires';
    syncAddTires();
  });

  function buildRearQty() {
    if (ui.rearQtyRow) return;
    var row;
    if (ui.qtyRow) {
      /* Clone = a NEW stepper element; upgrades cleanly on insertion. */
      row = ui.qtyRow.cloneNode(true);
      row.className = 'pdp-quantity-row pdp-gp-rear-qty';
      var lbl = row.querySelector('label');
      if (lbl) {
        lbl.removeAttribute('for');
        lbl.textContent = 'Rear Quantity';
      }
      ui.rearQtyLabel = lbl;
      var input = row.querySelector('input');
      if (input) {
        input.removeAttribute('id');
        input.removeAttribute('form');
        input.setAttribute('name', 'rear-quantity');
        input.min = '1';
        input.value = '2';
      }
    } else {
      row = el('div', 'pdp-quantity-row pdp-gp-rear-qty');
      row.innerHTML =
        '<label class="form-label">Rear Quantity</label>' +
        '<quantity-input class="pdp-quantity">' +
        '<button type="button" name="minus" class="qty-btn" aria-label="Decrease quantity">&minus;</button>' +
        '<input type="number" name="rear-quantity" class="qty-input" min="1" value="2">' +
        '<button type="button" name="plus" class="qty-btn" aria-label="Increase quantity">+</button>' +
        '</quantity-input>';
    }
    ui.qtyZone.appendChild(row);
    ui.rearQtyRow = row;
    if (!ui.rearQtyLabel) ui.rearQtyLabel = row.querySelector('label');
    ui.rearQtyInput = row.querySelector('input[name="rear-quantity"]') || row.querySelector('input');
    if (ui.rearQtyInput) {
      ui.rearQtyInput.addEventListener('change', function () { renderStgPrice(null); });
    }
  }

  /* ---------------------------------------------------------------
     Card render (incremental — never touches the adopted elements' identity)
     --------------------------------------------------------------- */

  function renderCard() {
    if (!built) return;

    var giantMode = state.giant && !state.vehicleId;
    var giantBlocked = giantMode && state.diaScope == null;
    if (ui.giantBox) {
      ui.giantBox.hidden = !giantMode;
      if (giantMode) renderGiantChips();
    }

    var rows = buildRows(visibleMembers());

    /* Owner 2026-08-18 r3: with a vehicle on a tire group, guaranteed fits
       list first, aggressive ("mods may be required") below, anything
       unlisted last — stable within each tier (keeps the rim/size sort). */
    if (groupIsTires() && state.vehicleId && pkgTireTiersLoaded && pkgTireTiers) {
      var ranked = rows.map(function (m, i) { return { m: m, i: i }; });
      ranked.sort(function (a, b) { return pkgTierRank(a.m) - pkgTierRank(b.m) || a.i - b.i; });
      rows = ranked.map(function (x) { return x.m; });
    }

    if (ui.frontList) {
      ui.frontList.wrap.hidden = giantBlocked;
      setListRows(ui.frontList, rows, state.sel);
      renderToggle(ui.frontList, state.sel);
    }
    if (cfg.sibling) renderFixedRow();
    renderMeta();

    /* Slot candidates. Hubs: rears for the current front pick (unchanged).
       Siblings: siblingStaggerPlan — rears when the page product has wider
       (or vehicle-fitting) rears, else FRONTS with the page product locked
       as the REAR (page-as-rear, owner 2026-08-20: the widest width of a
       group must still start a staggered set). A mode flip mid-engagement
       (vehicle set/cleared re-plans the slot) invalidates the pick below
       like any other stale-slot case. */
    var wasPageAsRear = state.pageAsRear;
    var slotRows;
    if (cfg.sibling) {
      var plan = state.sel
        ? siblingStaggerPlan(state.sel, rows, !!state.vehicleId)
        : { pageAsRear: false, rows: [] };
      state.pageAsRear = plan.pageAsRear;
      slotRows = plan.rows;
    } else {
      state.pageAsRear = false;
      slotRows = state.sel ? rearOptionsFor(state.sel, rows, !!state.vehicleId) : [];
    }
    /* Same guaranteed-first ordering for the slot list (the pool helpers
       re-sort by size internally, so re-rank their output too). */
    if (slotRows.length && groupIsTires() && state.vehicleId && pkgTireTiersLoaded && pkgTireTiers) {
      var rankedRear = slotRows.map(function (m, i) { return { m: m, i: i }; });
      rankedRear.sort(function (a, b) { return pkgTierRank(a.m) - pkgTierRank(b.m) || a.i - b.i; });
      slotRows = rankedRear.map(function (x) { return x.m; });
    }
    /* The rear slot shows whenever the GROUP runs staggered — even when the
       current front pick is already the widest at its diameter (the slot
       then guides instead of offering rows). Picking rows still requires
       actual candidates. SIBLING pages can't change the locked size, so a
       guiding-but-empty slot is a dead end there — the opener only shows
       when real candidates exist for the page product (either axle). */
    var groupStaggers = cfg.sibling
      ? (!!state.sel && slotRows.length > 0)
      : (!giantBlocked && staggeredEligible(rows));
    var canPickRear = groupStaggers && !!state.sel && slotRows.length > 0;

    /* Slot pick invalidated (front changed / refetch / re-filter / axle
       mode flipped) → fall back to the single setup so the quantity
       returns to a set of 4. */
    if (state.staggered &&
        (!canPickRear || !state.rearSel || state.pageAsRear !== wasPageAsRear ||
          indexOfPid(slotRows, state.rearSel.pid) === -1)) {
      state.staggered = false;
      state.rearSel = null;
      setQtyInput(ui.frontQtyInput, 4);
    }

    /* Page-as-rear flips every axle WORD (the locked row is the REAR, the
       slot picks the FRONT) — the sel/rearSel pairing itself never moves. */
    var pageAxle = state.pageAsRear ? 'Rear' : 'Front';
    var slotAxle = state.pageAsRear ? 'Front' : 'Rear';
    ui.frontLabel.textContent = (groupStaggers && state.rearOpen) ? pageAxle : 'Your Size';
    if (ui.frontRemove) ui.frontRemove.hidden = !state.sel;

    /* Implicit staggered: eligible groups show the opener; the slot block
       renders only once opened. Remove collapses back to the opener. */
    if (ui.stgBtnLabel) {
      ui.stgBtnLabel.textContent = 'Run It Staggered — Add ' + slotAxle + ' ' +
        (groupIsTires() ? 'Tires' : 'Wheels');
    }
    if (ui.rearHeadWord) ui.rearHeadWord.nodeValue = slotAxle;
    ui.stgBtn.hidden = !groupStaggers || state.rearOpen;
    ui.rearBlock.hidden = !groupStaggers || !state.rearOpen;
    if (groupStaggers && state.rearOpen) {
      ui.rearBlock.classList.toggle('is-engaged', state.staggered);
      ui.rearBlock.classList.toggle('is-exhausted', !canPickRear);
      var tireMode = groupIsTires();
      ui.stgHint.textContent = canPickRear
        ? (state.pageAsRear
          ? (tireMode
            ? 'Run it staggered — pick your front tire size.'
            : 'Run it staggered — pick a narrower front.')
          : (tireMode
            ? 'Run it staggered — pick your rear tire size.'
            : 'Run it staggered — go wider in the rear.'))
        : (tireMode
          ? 'No other rear sizes available for this setup.'
          : 'Pick a narrower front size at this diameter to run staggered.');
      ui.rearList.placeholder = 'Choose ' + slotAxle.toLowerCase() + ' size';
      var rt = ui.rearList.wrap.querySelector('.pdp-gp-toggle');
      if (rt) rt.disabled = !canPickRear;
      setListRows(ui.rearList, slotRows, state.rearSel);
      renderToggle(ui.rearList, state.rearSel);
    }
    ui.rearRemove.hidden = !state.rearOpen;
    /* After the invalidation block above — state.rearSel is final here. */
    renderRearMeta();
    if (ui.stgHint) ui.stgHint.hidden = state.staggered;
    if (ui.rearQtyRow) ui.rearQtyRow.hidden = !state.staggered;
    /* Two steppers in the qty zone while staggered — name the axles (the
       ADOPTED stepper always counts the page product, the cloned one the
       slot pick — page-as-rear only flips the words). */
    if (ui.frontQtyLabel) {
      ui.frontQtyLabel.textContent = state.staggered ? (pageAxle + ' Quantity') : 'Quantity';
    }
    if (ui.rearQtyLabel) {
      ui.rearQtyLabel.textContent = slotAxle + ' Quantity';
    }

    /* Square mode: the adopted .pdp-pricing (with all its hooks) shows.
       Staggered: hide it, show the card's own set-total block. */
    ui.pricing.style.display = state.staggered ? 'none' : '';
    ui.stgPrice.hidden = !state.staggered;
    if (state.staggered) renderStgPrice(rows);

    updateAtcState();
  }

  function renderGiantChips() {
    var box = ui.giantBox;
    while (box.firstChild) box.removeChild(box.firstChild);
    box.appendChild(el('p', 'pdp-group-picker__prompt-text',
      'Browse available sizes by diameter'));
    var chips = el('div', 'pdp-group-picker__chips');
    var seen = {};
    var diameters = [];
    state.members.forEach(function (m) {
      if (m.d == null || seen[m.d]) return;
      seen[m.d] = true;
      diameters.push(m.d);
    });
    diameters.sort(function (a, b) { return a - b; });
    diameters.forEach(function (d) {
      var chip = el('button',
        'pdp-group-picker__chip' + (state.diaScope === d ? ' is-active' : ''),
        d + '"');
      chip.type = 'button';
      chip.addEventListener('click', function () {
        state.diaScope = (state.diaScope === d) ? null : d;
        renderCard();
      });
      chips.appendChild(chip);
    });
    box.appendChild(chips);
  }

  /* --- Sibling locked row (the page's own size — no listbox) --- */

  function renderFixedRow() {
    if (!ui.fixedRow) return;
    var m = state.sel;
    while (ui.fixedRow.firstChild) ui.fixedRow.removeChild(ui.fixedRow.firstChild);
    if (!m) return;
    var left = el('span', 'pdp-gp-fixed-left');
    var sizeLine = el('span', 'pdp-gp-toggle-size', rowSizeText(m));
    if (m.cd) sizeLine.appendChild(el('span', 'pdp-gp-custom-pill', 'Custom'));
    left.appendChild(sizeLine);
    var sub = rowSubText(m);
    if (sub) left.appendChild(el('span', 'pdp-gp-toggle-sub', sub));
    ui.fixedRow.appendChild(left);
    var av = availInfo(m);
    /* Scarcity ("Only 2 left") is already the red .pdp-stock-alert under
       the price — repeating it here (in green) read as a contradiction. */
    if (!(av.low && m.ti === 1)) ui.fixedRow.appendChild(el('span', 'pdp-gp-opt-avail' + (av.ok ? ' is-ok' : ' is-out'), av.text));
  }

  /* --- Per-selection meta row (PART # / BORE, under the front toggle) --- */

  function renderMetaInto(m, rowEl, skuEl, boreEl) {
    if (!rowEl) return;
    var hasSku = !!(m && m.sku);
    /* BORE is a wheels concept — omitted on tire pages. */
    var hasBore = !groupIsTires() && !!(m && m.hb != null && m.hb !== '');
    rowEl.hidden = !hasSku && !hasBore;
    skuEl.hidden = !hasSku;
    if (hasSku) skuEl.textContent = 'PART # ' + m.sku;
    boreEl.hidden = !hasBore;
    if (hasBore) boreEl.textContent = 'BORE ' + m.hb + 'mm';
  }

  function renderMeta() {
    renderMetaInto(state.sel, ui.metaRow, ui.metaSku, ui.metaBore);
  }

  /* Rear mirrors the front once a rear size is engaged. */
  function renderRearMeta() {
    renderMetaInto(state.rearSel, ui.rearMetaRow, ui.rearMetaSku, ui.rearMetaBore);
  }

  function copySkuFlash(m, skuEl, rerender) {
    if (!m || !m.sku || !skuEl) return;
    function flash() {
      skuEl.textContent = 'Copied!';
      skuEl.classList.add('is-copied');
      setTimeout(function () {
        skuEl.classList.remove('is-copied');
        rerender();
      }, 1200);
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(m.sku)).then(flash, function () { /* denied — keep quiet */ });
        return;
      }
    } catch (e) { /* fall through to execCommand */ }
    try {
      var ta = document.createElement('textarea');
      ta.value = String(m.sku);
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      flash();
    } catch (e2) { /* non-fatal */ }
  }

  function copySelectedSku() {
    copySkuFlash(state.sel, ui.metaSku, renderMeta);
  }

  function copyRearSku() {
    copySkuFlash(state.rearSel, ui.rearMetaSku, renderRearMeta);
  }

  function renderStgPrice(rows) {
    if (!built || !state.staggered) return;
    var box = ui.stgPrice;
    while (box.firstChild) box.removeChild(box.firstChild);
    /* Axle mapping: the ADOPTED stepper always counts state.sel and the
       cloned one state.rearSel — page-as-rear only swaps which AXLE each
       pair IS, so the Front:/Rear: lines below stay truthful. */
    var inv = !!state.pageAsRear;
    var f = inv ? state.rearSel : state.sel;
    var r = inv ? state.sel : state.rearSel;
    if (f && r) {
      var fq = readQty(inv ? ui.rearQtyInput : ui.frontQtyInput, 2);
      var rq = readQty(inv ? ui.frontQtyInput : ui.rearQtyInput, 2);
      var fPrice = toCents(f.price);
      var rPrice = toCents(r.price);
      var totalCents = (fq * fPrice) + (rq * rPrice);
      /* Sale savings on the staggered total: members carry `cap` (compare-at)
         when on sale — front + rear each, falling back to price when not. */
      var fCap = (f.cap != null) ? toCents(f.cap) : fPrice;
      var rCap = (r.cap != null) ? toCents(r.cap) : rPrice;
      var capTotalCents = (fq * fCap) + (rq * rCap);
      var savingsCents = capTotalCents - totalCents;
      var onSale = savingsCents > 0;

      var main = el('div', 'pdp-gp-stg-main');
      main.appendChild(el('strong', 'pdp-gp-stg-total', money(totalCents)));
      main.appendChild(el('span', 'pdp-gp-stg-label', 'set total'));
      if (onSale) main.appendChild(el('s', 'pdp-gp-stg-compare', money(capTotalCents)));
      box.appendChild(main);
      if (onSale) {
        var sv = el('div', 'pdp-savings');
        sv.setAttribute('data-savings', '');
        sv.appendChild(el('span', 'pdp-savings__chip', 'Save'));
        sv.appendChild(el('strong', 'pdp-savings__amount pdp-savings__amount--set', money(savingsCents)));
        sv.appendChild(el('span', 'pdp-savings__label', 'on this staggered set'));
        box.appendChild(sv);
      }
      box.appendChild(el('div', 'pdp-gp-stg-axles',
        'Front: ' + fq + ' × ' + money(fPrice) +
        '  ·  Rear: ' + rq + ' × ' + money(rPrice)));
      box.appendChild(el('div', 'pdp-gp-stg-inst',
        'or 4 interest-free payments of ' + money(Math.floor(totalCents / 4))));
    } else {
      var pool = (rows && rows.length) ? rows : buildRows(visibleMembers());
      if (!pool.length) pool = state.members;
      var min = null;
      var max = null;
      pool.forEach(function (m) {
        var c = toCents(m.price);
        if (isNaN(c)) return;
        if (min == null || c < min) min = c;
        if (max == null || c > max) max = c;
      });
      var range = el('div', 'pdp-gp-stg-main');
      var textVal = '';
      if (min != null) {
        textVal = (min === max) ? moneyRange(min) : moneyRange(min) + ' – ' + moneyRange(max);
      }
      range.appendChild(el('strong', 'pdp-gp-stg-total', textVal));
      range.appendChild(el('span', 'pdp-gp-stg-label', 'per ' + unitNoun()));
      box.appendChild(range);
      box.appendChild(el('div', 'pdp-gp-stg-inst',
        'Pick front and rear sizes to see your staggered set total.'));
    }
  }

  function updateAtcState() {
    var btn = ui.buyButtons ? ui.buyButtons.querySelector('.pdp-add-cart') : null;
    if (!btn) return;
    var enabled;
    var soldOutText = false;
    if (state.staggered) {
      enabled = !!(state.sel && state.rearSel) &&
        memberAvailable(state.sel) && memberAvailable(state.rearSel);
    } else {
      enabled = !!state.sel && memberAvailable(state.sel);
      soldOutText = !!state.sel && !memberAvailable(state.sel);
    }
    btn.disabled = !enabled;
    if (ui.lastSoldOut !== soldOutText) {
      ui.lastSoldOut = soldOutText;
      var btnText = btn.querySelector('.btn-text');
      if (btnText) {
        var icon = btnText.querySelector('svg');
        btnText.textContent = soldOutText ? ' Sold Out' : ' Add to Cart';
        if (icon) btnText.insertBefore(icon, btnText.firstChild);
      }
    }
  }

  function showCardError(msg) {
    if (!ui.error) return;
    ui.error.textContent = msg;
    ui.error.hidden = false;
  }

  function hideCardError() {
    if (!ui.error) return;
    ui.error.hidden = true;
  }

  /* ---------------------------------------------------------------
     Mode transitions
     --------------------------------------------------------------- */

  function clearFront() {
    state.sel = null;
    hideCardError();
    renderCard();
  }

  /* Picking a rear engages staggered mode: quantities shift 4 → 2+2 on
     first engagement (re-picks keep whatever the buyer set). */
  function engageRear(m) {
    if (!built) return;
    if (!state.staggered) {
      state.staggered = true;
      buildRearQty();
      if (ui.rearQtyInput) ui.rearQtyInput.value = '2';
      /* Staggered default: 2 front + 2 rear. */
      setQtyInput(ui.frontQtyInput, 2);
    }
    state.rearSel = m;
    hideCardError();
    renderCard();
    /* Selection contract: rear member + the 2/2 qty split (Stage 3
       Task 1) — front unchanged, so re-sync from the CURRENT front
       selection to pick up the new rear/staggered state. */
    if (state.sel) syncGroupSelectionGlobal(state.sel, {});
  }

  /* Rear Remove: back to the single (square) setup — set of 4. The rear
     block stays visible (it's eligibility-driven), just disengaged. */
  function disengageRear() {
    state.staggered = false;
    state.rearSel = null;
    state.rearOpen = false;
    setQtyInput(ui.frontQtyInput, 4);
    hideCardError();
    renderCard();
    /* Selection contract: rear:null, qty restored to 4 (Stage 3 Task 1). */
    if (state.sel) syncGroupSelectionGlobal(state.sel, {});
  }

  /* ---------------------------------------------------------------
     Auto-select on vehicle (owner requirement): if the current pick doesn't
     fit (or got filtered out), move to the best fitting member.
     --------------------------------------------------------------- */

  function maybeAutoSelect(cur) {
    /* Sibling pages never leave the page product — the fitment widget owns
       the no-fit messaging (banner + canonical-hub redirect rescue). */
    if (cfg.sibling) return null;
    if (!state.vehicleId || state.showAll) return null;
    var visible = filterByFit(state.members, false);
    if (pkgScope) visible = visible.filter(pkgScopeAllows);
    // Mirror visibleMembers' tire sold-out hiding — never auto-select a row
    // the listbox doesn't show.
    visible = visible.filter(function (m) { return !tireSizeOf(m) || memberAvailable(m); });
    if (!visible.length) return null;
    var inList = !!cur && indexOfPid(visible, cur.pid) !== -1;
    var dnf = !!cur && !!cur.fit && cur.fit.overall === 'does_not_fit';
    if (inList && !dnf) return null;
    var best = bestFit(visible, cur);
    return (best && (!cur || best.pid !== cur.pid)) ? best : null;
  }

  /* ---------------------------------------------------------------
     Staggered add-to-cart (sequential front → rear via CartAPI)
     --------------------------------------------------------------- */

  function showAtcSuccess(btn) {
    var btnText = btn.querySelector('.btn-text');
    if (!btnText) return;
    var original = btnText.textContent;
    var icon = btnText.querySelector('svg');
    btnText.textContent = 'Added!';
    if (icon) btnText.insertBefore(icon, btnText.firstChild);
    btn.classList.add('btn--success');
    setTimeout(function () {
      btnText.textContent = original;
      if (icon) btnText.insertBefore(icon, btnText.firstChild);
      btn.classList.remove('btn--success');
    }, 2000);
  }

  function handleStaggeredAdd() {
    var btn = ui.buyButtons ? ui.buyButtons.querySelector('.pdp-add-cart') : null;
    if (!btn || btn.disabled || btn.classList.contains('loading')) return;
    /* Page-as-rear: the page product (adopted stepper) IS the rear and the
       slot pick (cloned stepper) the front. Mapping here keeps the add
       order front-first in both modes — downstream cart grouping and the
       per-axle verdict stamps below always see F then R. */
    var inv = !!state.pageAsRear;
    var front = inv ? state.rearSel : state.sel;
    var rear = inv ? state.sel : state.rearSel;
    var fq = readQty(inv ? ui.rearQtyInput : ui.frontQtyInput, 0);
    var rq = readQty(inv ? ui.frontQtyInput : ui.rearQtyInput, 0);
    if (!front || !rear || fq < 1 || rq < 1) {
      showCardError('Select both front and rear ' + unitNoun() + ' sizes first.');
      return;
    }
    /* Owner spec 2026-08-18: in package-draft mode (tire PDP mid-flow) a
       staggered pick routes to the builder like every other submit — the
       package is reviewed before anything touches the cart. The hook is
       installed by pdp-package-price.liquid, which owns draft mode; this
       capture-phase intercept would otherwise bypass its form interceptor
       (the known ordering trap between the two listeners). */
    if (document.documentElement.classList.contains('pdp-pkg-draft')
        && typeof window.__TFS_PKG_GO_REVIEW__ === 'function') {
      window.__TFS_PKG_GO_REVIEW__(
        { pid: front.pid, vid: front.vid, qty: fq },
        { pid: rear.pid, vid: rear.vid, qty: rq }
      );
      return;
    }
    var api = cartApi();
    if (!api) {
      showCardError('Could not add to cart. Please refresh and try again.');
      return;
    }
    hideCardError();
    btn.classList.add('loading');
    btn.setAttribute('aria-busy', 'true');

    var hadVehicle = !!state.vehicleId;
    var prevResult = window.__TFS_FITMENT_RESULT__;

    /* cart-interceptor stamps the cart line's Verdict from this global at
       add time — front add carries the FRONT member's front verdict, rear
       add the REAR member's rear verdict. No vehicle → leave it alone. */
    function stamp(m, pos) {
      if (!hadVehicle) return;
      window.__TFS_FITMENT_RESULT__ = {
        fitType: (m.fit && m.fit[pos]) || 'no_data',
        productId: m.pid,
        source: 'group-picker'
      };
    }
    function done() {
      if (hadVehicle) window.__TFS_FITMENT_RESULT__ = prevResult;
      btn.classList.remove('loading');
      btn.removeAttribute('aria-busy');
    }

    stamp(front, 'front');
    api.add([{ id: front.vid, quantity: fq }]).then(function () {
      stamp(rear, 'rear');
      return api.add([{ id: rear.vid, quantity: rq }]);
    }).then(function () {
      done();
      showAtcSuccess(btn);
    }).catch(function (err) {
      done();
      var msg = (err && err.message) || 'Could not add to cart. Please try again.';
      if (window.TFSToast) window.TFSToast.show(msg, { tone: err && err.partial ? 'warning' : 'error' });
      else showCardError(msg);
    });
  }

  /* ---------------------------------------------------------------
     Selection → page re-render
     --------------------------------------------------------------- */

  /* One member → the selection-contract shape (Stage 3 Task 1). qty falls
     back to the CURRENT qty input value when present, else the axle's
     square/staggered default (4 front, 2/2 when staggered) — read live
     rather than cached so a buyer's manual qty edit is reflected even
     though nothing re-calls this on plain qty-input change (matches the
     rest of the file: qty inputs are only ever read on demand, e.g.
     renderStgPrice/handleStaggeredAdd).

     priceCents (Stage 3 T9 anomaly 1/3 fix): the member's OWN price, off
     the /group payload the picker already holds for row rendering — the
     exact same `m.price` field setListRows uses to paint the listbox
     price column (rowSizeText/right column, ~setListRows above) and
     updatePriceZone uses to paint the price zone. That field is DOLLARS
     (see toCents()/money(toCents(m.price)) call sites throughout this
     file) — converted here to integer cents via the same toCents() used
     everywhere else, so consumers get money truth straight from the
     payload instead of re-parsing rendered DOM text (which this file and
     pdp-package-price.liquid both asynchronously overwrite — reading it
     back is a race, not a read). Math.round'd defensively; NaN‑safe
     (toCents(undefined) → NaN, and finite-checked by every consumer). */
  function memberSelectionEntry(m, defaultQty, input) {
    if (!m) return null;
    return {
      pid: m.pid,
      vid: m.vid,
      qty: readQty(input, defaultQty),
      dia: memberDia(m),
      width: memberWidth(m),
      priceCents: toCents(m.price)
    };
  }

  /* Builds + writes the EXTENDED __TFS_GP_SELECTION__ contract and fires
     tfs:group-selection. Single choke point for every mutation that
     changes "what's selected" — applySelection (front change, including
     the initial render), engageRear, disengageRear — so the global and
     the event can never drift from each other or from state.

     Legacy shape PRESERVED verbatim: productId/handle/at are the fields
     the fitment embed's handshake already reads (grep-verified — only
     .productId is actually consumed today, .handle/.at ride along
     unused but kept for any other/future consumer). front/rear/type are
     ADDITIVE. Same discipline on the event: the existing detail fields
     (productId/variantId/handle/available/initial) keep describing the
     FRONT member exactly as before; `rear` is a new, additive field.
     `front` (mirroring `rear`) is ALSO added to the event detail as of
     T9 — front.priceCents is the money-truth field
     pdp-package-price.liquid now bases its overlay math on; every other
     front.* field was already reachable off the global written just
     above (same tick, no ordering change), so this purely adds the one
     field the price snippet actually needs without requiring it to read
     __TFS_GP_SELECTION__ separately. The global-before-dispatch order
     (Stage 3 T1 property) is unchanged — front/rear are computed once,
     above, and reused for both the global write and the event detail. */
  function syncGroupSelectionGlobal(m, opts) {
    opts = opts || {};
    var staggered = !!state.staggered && !!state.rearSel;
    var frontDefaultQty = staggered ? 2 : 4;
    /* Page-as-rear (sibling, owner 2026-08-20): `m` — the page product,
       paired with the ADOPTED qty input — is the REAR axle and the slot
       pick the FRONT. Consumers key on front/rear MEANING (pdp-cross-sell
       emitSlots writes front → untagged f-slot, rear → r:), so the two
       entries swap; the legacy top-level fields keep describing `m`
       exactly as before in every mode. */
    var inv = staggered && !!state.pageAsRear;
    var front = inv
      ? memberSelectionEntry(state.rearSel, 2, ui.rearQtyInput)
      : memberSelectionEntry(m, frontDefaultQty, ui.frontQtyInput);
    var rear = staggered
      ? (inv
        ? memberSelectionEntry(m, 2, ui.frontQtyInput)
        : memberSelectionEntry(state.rearSel, 2, ui.rearQtyInput))
      : null;
    var type = groupIsTires() ? 'tires' : 'wheels';

    try {
      window.__TFS_GP_SELECTION__ = {
        productId: m.pid,
        handle: m.handle,
        at: Date.now(),
        front: front,
        rear: rear,
        type: type
      };
    } catch (e) { /* ignore */ }

    try {
      document.dispatchEvent(new CustomEvent('tfs:group-selection', {
        detail: {
          productId: m.pid,
          variantId: m.vid,
          handle: m.handle,
          available: memberAvailable(m),
          initial: !!opts.initial,
          front: front,
          rear: rear
        }
      }));
    } catch (e) { /* CustomEvent unavailable — non-fatal */ }
  }

  function applySelection(m, opts) {
    opts = opts || {};
    state.sel = m;
    renderCard();
    renderFinishes();

    /* Record + announce EVERY applied selection — including the initial one
       (2026-07-07). The embed can commit a verdict for the landing product
       before the (possibly slow) group payload arrives, or its own /check
       can fail under DB load; without this signal a wrong "DOES NOT FIT"
       sticks until the shopper manually re-picks a size. The embed's
       listener skips redundant same-product re-checks unless it is
       currently showing no-fit. */
    syncGroupSelectionGlobal(m, opts);

    /* Early-return ONLY when the server-rendered page already shows this
       exact member (initial load of the landing product). An initial
       filter-context preselect of a DIFFERENT member falls through to the
       full re-render below — price zone, gallery, specs and the ATC
       variant input must all describe the preselected member (owner bug
       2026-08-19: hub price + hub variant went to cart while the dropdown
       showed the preselected sibling). */
    if (initialRenderAlreadyCorrect(opts.initial, m.pid, cfg.pid)) return;

    updatePriceZone(m);
    updateStockAlert(m);
    capQtyToStock(m);
    updateNotices(m);
    updatePackageBadge(m);
    updatePkgHook(m);
    updateGallery(m);
    updateSpecs(m);

    var input = document.querySelector('input[data-variant-id]');
    if (input && m.vid != null) input.value = String(m.vid);

    /* Cross-system sync: cart-interceptor stamps Verdict from this global.
       The fitment embed computes a richer result (reasons, accessoryData —
       drives lug-kit auto-add) for the LANDING product, so we snapshot it
       before the first sibling overwrite and restore it when the buyer
       selects the landing product again. */
    if (state.vehicleId) {
      if (m.pid === cfg.pid) {
        if (embedResultSnapshot !== undefined) {
          window.__TFS_FITMENT_RESULT__ = embedResultSnapshot;
          embedResultSnapshot = undefined;
        }
      } else {
        var currResult = window.__TFS_FITMENT_RESULT__;
        if (embedResultSnapshot === undefined && (!currResult || currResult.source !== 'group-picker')) {
          embedResultSnapshot = currResult || null;
        }
        window.__TFS_FITMENT_RESULT__ = {
          fitType: m.fit && m.fit.overall ? m.fit.overall : 'no_data',
          productId: m.pid,
          source: 'group-picker'
        };
      }
    }
    /* (Selection recording + tfs:group-selection dispatch moved ABOVE the
       initial-return so the embed hears about every applied selection.) */

    /* Sibling pages keep their own URL — the selection never changes.
       Initial preselects also keep the landing URL: the shopper hasn't
       interacted yet, the selectPid/facet params already reproduce this
       selection on reload, and pushing here would insert a junk history
       entry (Back would re-land on the hub URL instead of the grid). */
    if (!cfg.sibling && !opts.fromPop && !opts.initial) {
      var path = '/products/' + m.handle;
      if (window.location.pathname !== path) {
        try {
          window.history.pushState({}, '', path + window.location.search);
        } catch (e) { /* sandboxed — fine */ }
      }
    }
  }

  function updatePriceZone(m) {
    var priceCents = toCents(m.price);
    var capCents = m.cap != null ? toCents(m.cap) : null;
    var onSale = capCents != null && capCents > priceCents;

    var priceEl = document.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.textContent = money(priceCents);
      priceEl.classList.toggle('pdp-price-current--sale', onSale);
    }

    var setQty = 4;
    var setEl = document.querySelector('[data-set-price]');
    if (setEl) {
      var setWrap = setEl.closest('.pdp-price-set');
      if (setWrap && setWrap.getAttribute('data-set-qty')) {
        setQty = parseInt(setWrap.getAttribute('data-set-qty'), 10) || 4;
      }
      setEl.textContent = money(priceCents * setQty);
    }

    var instEl = document.querySelector('[data-installment]');
    if (instEl) {
      var instWrap = instEl.closest('.pdp-installments');
      var instQty = (instWrap && instWrap.getAttribute('data-set-qty'))
        ? parseInt(instWrap.getAttribute('data-set-qty'), 10) || 4
        : 4;
      instEl.textContent = money(Math.floor(priceCents * instQty / 4));
    }

    var compareEl = document.querySelector('.pdp-price-compare');
    if (!compareEl && onSale && priceEl) {
      compareEl = document.createElement('s');
      compareEl.className = 'pdp-price-compare';
      priceEl.parentNode.insertBefore(compareEl, priceEl);
    }
    if (compareEl) {
      if (onSale) {
        compareEl.textContent = money(capCents);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    var saleBadge = document.querySelector('[data-badge-sale]');
    if (saleBadge) {
      saleBadge.style.display = onSale ? '' : 'none';
      if (onSale) {
        var pctEl = saleBadge.querySelector('[data-badge-sale-pct]');
        if (pctEl) {
          var pct = Math.round(((capCents - priceCents) / capCents) * 100);
          pctEl.textContent = '−' + pct + '% off!';
        }
      }
    }

    updateSavings(onSale, priceCents, capCents, setQty, !!setEl);
  }

  function updateSavings(onSale, priceCents, capCents, setQty, hasSetPrice) {
    var savings = document.querySelector('.pdp-savings');
    var savingsCents = onSale ? capCents - priceCents : 0;
    if (!savings && onSale && savingsCents > 0) {
      var pricing = document.getElementById('price-' + cfg.sectionId) ||
        document.querySelector('.pdp-pricing');
      if (!pricing) return;
      savings = el('div', 'pdp-savings');
      savings.setAttribute('data-savings', '');
      savings.appendChild(el('span', 'pdp-savings__chip', 'Save'));
      savings.appendChild(el('strong',
        'pdp-savings__amount' + (hasSetPrice ? ' pdp-savings__amount--set' : ''), ''));
      savings.appendChild(el('span', 'pdp-savings__label',
        hasSetPrice ? 'on a set of ' + setQty : 'per ' + unitNoun()));
      /* Markup order: price row → savings chip → installments teaser. Slot
         the chip right after the price row (before the pay-in-4 anchor). */
      var mainRow = pricing.querySelector('.pdp-pricing__main');
      var anchor = pricing.querySelector('.pdp-installments');
      if (mainRow && mainRow.nextSibling) pricing.insertBefore(savings, mainRow.nextSibling);
      else if (anchor) pricing.insertBefore(savings, anchor);
      else pricing.appendChild(savings);
    }
    if (!savings) return;
    if (!onSale || savingsCents <= 0) {
      savings.style.display = 'none';
      return;
    }
    savings.style.display = '';
    var amountEl = savings.querySelector('.pdp-savings__amount');
    if (amountEl) {
      var isSet = amountEl.classList.contains('pdp-savings__amount--set');
      amountEl.textContent = money(isSet ? savingsCents * setQty : savingsCents);
    }
  }

  function updateStockAlert(m) {
    var alertEl = document.querySelector('.pdp-stock-alert');
    var show = memberAvailable(m) && m.ti === 1 && m.qty > 0 && m.qty < 12;
    if (!alertEl) {
      if (!show) return;
      var pricing = document.getElementById('price-' + cfg.sectionId) ||
        document.querySelector('.pdp-pricing');
      if (!pricing) return;
      alertEl = el('div', 'pdp-stock-alert');
      pricing.appendChild(alertEl);
    }
    if (!show) {
      alertEl.style.display = 'none';
      return;
    }
    alertEl.style.display = '';
    alertEl.setAttribute('data-inventory-qty', String(m.qty));
    if (m.qty < 4) alertEl.textContent = 'Only ' + m.qty + ' ' + unitNoun() + (m.qty !== 1 ? 's' : '') + ' left!';
    else if (m.qty < 8) alertEl.textContent = 'Only 1 set left!';
    else alertEl.textContent = 'Only 2 sets left!';
  }

  /* Keep the square-mode qty within what Shopify will actually add for a
     tracked member (it refuses the rest with "Only N items were added").
     Mirrors the Liquid cap in main-product-wt.liquid; switching back to a
     well-stocked size restores the block's default qty. */
  function capQtyToStock(m) {
    var input = ui.frontQtyInput;
    if (!input || state.rearSel) return;
    var def = parseInt(input.getAttribute('data-default-qty'), 10) || 4;
    var cap = (m && m.ti === 1 && m.qty > 0) ? m.qty : 0;
    var cur = readQty(input, def);
    if (cap) input.setAttribute('max', String(cap));
    else input.removeAttribute('max');
    var next = cur;
    if (cap && cur > cap) next = cap;
    else if (cur < def && input.getAttribute('data-qty-capped') === '1') next = cap ? Math.min(def, cap) : def;
    if (next !== cur) setQtyInput(input, next);
    input.setAttribute('data-qty-capped', cap && cap < def ? '1' : '0');
  }

  function pageVendorUpper() {
    var brand = document.querySelector('.pdp-brand a');
    return brand ? (brand.textContent || '').trim().toUpperCase() : 'THIS';
  }

  function createNotice(extraClass, text) {
    var host = document.querySelector('.pdp-buy-buttons');
    if (!host) return null;
    var box = el('div', 'custom-notice ' + extraClass);
    box.appendChild(el('p', null, text));
    host.appendChild(box);
    return box;
  }

  function updateNotices(m) {
    var cdEl = document.querySelector('.custom-drill-notice');
    var mtoEl = document.querySelector('.made-to-order-notice');
    var snEl = document.querySelector('.product-special-note');

    if (!cdEl && m.cd) {
      cdEl = createNotice('custom-drill-notice',
        '⚠️ THIS ' + pageVendorUpper() + ' WHEEL IS CUSTOM DRILLED TO FIT YOUR VEHICLE AND WILL REQUIRE ADDITIONAL PROCESSING TIME.');
    }
    /* Liquid renders MTO as the elsif of custom-drill — mirror that priority. */
    if (!mtoEl && m.mto && !m.cd) {
      mtoEl = createNotice('made-to-order-notice',
        '⚠️ ' + pageVendorUpper() + ' WHEELS ARE MADE TO ORDER AND WILL REQUIRE ADDITIONAL TIME TO MANUFACTURE.');
    }
    if (cdEl) cdEl.style.display = m.cd ? '' : 'none';
    if (mtoEl) mtoEl.style.display = (m.mto && !m.cd) ? '' : 'none';
    /* Sibling special-notes TEXT isn't in the payload — we can only hide the
       landing product's note when the selected sibling doesn't carry one. */
    if (snEl) snEl.style.display = m.sn ? '' : 'none';
  }

  function updatePackageBadge(m) {
    var badge = document.querySelector('[data-pkg-badge]');
    if (!badge) return;
    // Owner 2026-08-18 r3: package discount retired — the "save up to $X"
    // badge never shows (the snippet is orphaned on live templates anyway;
    // this is the belt in case it ever gets re-rendered).
    badge.style.display = 'none';
  }

  /* Package hook under the buy buttons ("Save up to $X when you add
     tires") — Liquid renders it for the landing product; keep it in step
     with the selected member's pkg payload, hiding the line entirely when
     the selection carries no live package deal.

     Stage 3 Task 1: when the /group fetch carried pkgW/pkgT, m.pkg.front
     is the PRECISE per-axle figure for the buyer's actual anchor (the
     legacy m.pkg.ss is a group-level CEILING — the best any member in the
     group could do, not necessarily what pairs with what the shopper has
     selected elsewhere). Prefer front.discountSet whenever that axle
     resolved eligible; fall back to the legacy ss ceiling exactly as
     before otherwise (no pkgW/pkgT on the request → m.pkg.front is
     undefined → this is byte-identical to the pre-Task-1 behavior). */
  function updatePkgHook(m) {
    var hook = document.querySelector('[data-pkg-hook]');
    if (!hook) return;
    // Owner 2026-08-18 r3: package discount retired at every level — the
    // "Save up to $X when you add tires" hook never shows anywhere.
    hook.hidden = true;
    if (true) return;
    var front = m.pkg && m.pkg.front;
    var amountDollars = (front && front.eligible && front.discountSet > 0)
      ? front.discountSet
      : (m.pkg && m.pkg.ss > 0 ? m.pkg.ss : 0);
    var live = amountDollars > 0;
    hook.hidden = !live;
    if (live) {
      var amt = hook.querySelector('[data-pkg-hook-amount]');
      if (amt) amt.textContent = '$' + Math.round(amountDollars);
    }
  }

  function updateGallery(m) {
    if (!m.img) return;
    var img = document.getElementById('pdp-main-img-' + cfg.sectionId) ||
      document.querySelector('.pdp-main-image img');
    if (!img) return;
    if (img.getAttribute('src') === m.img) return;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = m.img;
    if (m.title) img.alt = m.title;
  }

  /* NOTE: the H1 / document.title are deliberately NOT touched on selection —
     the merged PDP carries a static group-level title (Brand + Model +
     Finish), server-rendered in Liquid. */

  function setSpecRow(label, value) {
    if (value == null || value === '') return;
    var rows = document.querySelectorAll('.specs-table tr');
    for (var i = 0; i < rows.length; i++) {
      var cells = rows[i].querySelectorAll('td');
      if (cells.length < 2) continue;
      if ((cells[0].textContent || '').trim() !== label) continue;
      var target = cells[cells.length - 1];
      var link = target.querySelector('a');
      if (link) link.textContent = String(value);
      else target.textContent = String(value);
      return;
    }
  }

  function updateSpecs(m) {
    var skuEl = document.querySelector('.specs-table [data-spec="sku"]');
    if (skuEl && m.sku) skuEl.textContent = m.sku;
    var ts = tireSizeOf(m);
    if (ts) setSpecRow('Tire Size', ts);
    if (m.d != null) setSpecRow('Diameter', m.d + '"');
    if (m.w != null) setSpecRow('Width', m.w);
    if (m.o != null) setSpecRow('Offset', m.o);
    if (m.bp) setSpecRow('Bolt Pattern', String(m.bp).split(',').join(', '));
    if (m.hb != null) setSpecRow('Centerbore / Hub', m.hb + 'mm');
  }

  /* ---------------------------------------------------------------
     Other Available Finishes rail — image + finish name + stock badge only
     --------------------------------------------------------------- */

  function renderFinishes() {
    var wrap = document.getElementById('pdp-other-finishes');
    if (!wrap || !state.group) return;
    var rail = wrap.querySelector('[data-finishes-rail]');
    if (!rail) return;

    var siblings = (state.group.finishes || []).filter(function (f) {
      return f && f.handle && f.groupKey !== state.group.groupKey;
    });
    if (!siblings.length) {
      wrap.hidden = true;
      return;
    }

    while (rail.firstChild) rail.removeChild(rail.firstChild);

    siblings.forEach(function (f) {
      /* Sibling (per-size) pages deep-link each finish to the SAME
         size/bp/offset when that finish stocks it (payload finishes[].spec,
         2026-07-06); finishes without this size fall back to the hub, whose
         picker re-picks. Hubs always link hubs. The stock badge follows the
         link target: exact-spec availability on deep links, group-level on
         hub links. Older cached payloads lack .spec — hub fallback. */
      var spec = (cfg.sibling && f.spec && f.spec.handle) ? f.spec : null;
      var card = document.createElement('a');
      card.className = 'pdp-other-finishes__card';
      /* Stage 3 Task 1 item 2: forward package/vehicle context so a
         shopper mid package-flow doesn't lose it clicking a finish. No-op
         on the default no-params PDP (urlWithPackageContext returns the
         href unchanged when none of the forwarded params are present). */
      card.href = urlWithPackageContext('/products/' + (spec ? spec.handle : f.handle));

      var media = el('span', 'pdp-other-finishes__media');
      if (f.img) {
        var img = document.createElement('img');
        img.src = f.img;
        img.alt = f.finish || 'Wheel finish';
        img.loading = 'lazy';
        img.width = 120;
        img.height = 120;
        media.appendChild(img);
      }
      card.appendChild(media);

      card.appendChild(el('span', 'pdp-other-finishes__name', f.finish || 'Finish'));

      var avail = spec ? !!spec.inStock : !!f.inStock;
      if (!avail) {
        card.appendChild(el('span',
          'pdp-other-finishes__badge pdp-other-finishes__badge--oos', 'Sold out'));
      } else {
        card.appendChild(el('span',
          'pdp-other-finishes__badge pdp-other-finishes__badge--ok', 'In stock'));
      }

      rail.appendChild(card);
    });

    wrap.hidden = false;
    rail.scrollLeft = 0;
    initFinishesNav(wrap, rail);
  }

  /* Prev/next arrows + edge fades for the finishes rail — on desktop the
     only overflow cue was a thin native scrollbar, so cards past the edge
     read as cut off. Arrows show only while the rail actually overflows. */
  function initFinishesNav(wrap, rail) {
    var nav = wrap.querySelector('.pdp-other-finishes__nav');
    if (!nav) {
      nav = el('div', 'pdp-other-finishes__nav');
      var prev = el('button', 'pdp-other-finishes__arrow', '\u2039');
      var next = el('button', 'pdp-other-finishes__arrow', '\u203A');
      prev.type = next.type = 'button';
      prev.setAttribute('aria-label', 'Previous finishes');
      next.setAttribute('aria-label', 'More finishes');
      prev.setAttribute('data-dir', '-1');
      next.setAttribute('data-dir', '1');
      nav.appendChild(prev);
      nav.appendChild(next);
      var title = wrap.querySelector('.pdp-other-finishes__title');
      if (title) title.appendChild(nav);
      else wrap.insertBefore(nav, rail);
      nav.addEventListener('click', function (e) {
        var b = e.target.closest && e.target.closest('[data-dir]');
        if (!b) return;
        var step = Math.max(rail.clientWidth - 120, 140) * Number(b.getAttribute('data-dir'));
        try { rail.scrollBy({ left: step, behavior: 'smooth' }); } catch (err) { rail.scrollLeft += step; }
      });
      rail.addEventListener('scroll', function () { syncFinishesNav(wrap, rail); }, { passive: true });
      window.addEventListener('resize', function () { syncFinishesNav(wrap, rail); });
    }
    syncFinishesNav(wrap, rail);
  }

  function syncFinishesNav(wrap, rail) {
    var max = rail.scrollWidth - rail.clientWidth;
    var overflow = max > 4;
    var atStart = rail.scrollLeft <= 2;
    var atEnd = rail.scrollLeft >= max - 2;
    wrap.classList.toggle('pdp-other-finishes--overflow', overflow);
    wrap.classList.toggle('pdp-other-finishes--at-start', !overflow || atStart);
    wrap.classList.toggle('pdp-other-finishes--at-end', !overflow || atEnd);
    var btns = wrap.querySelectorAll('.pdp-other-finishes__arrow');
    if (btns.length === 2) {
      btns[0].disabled = !overflow || atStart;
      btns[1].disabled = !overflow || atEnd;
    }
  }

  /* ---------------------------------------------------------------
     Boot / lifecycle
     --------------------------------------------------------------- */

  function failOpen() {
    /* First fetch failed: drop the shimmer, take no space, leave the
       server-rendered product fully purchasable. Nothing was adopted yet.
       If the card is already live (a REFETCH failed), keep serving the last
       good group — hiding it would take the adopted price/ATC with it. */
    if (built) return;
    while (root.firstChild) root.removeChild(root.firstChild);
    root.hidden = true;
    var finishes = document.getElementById('pdp-other-finishes');
    if (finishes) finishes.hidden = true;
  }

  /* tfs:pkg-annotations (Stage 3 Task 1 item 6) — display-safe-only members
     list for Task 3's overlay notice (chip-picking eligible sizes). Fires
     after EVERY /group load, even when nothing was annotated (pkg.front
     absent on every member) — consumers gate on there being any eligible
     entries, not on the event firing at all. anchorTitle is intentionally
     omitted here: the group payload carries no wheel/tire product title
     for the OTHER type's anchor (same data-gap as the {dia}×{w} copy
     above) — Task 3 resolves it another way if needed.

     T9 anomaly 2 fix — ORDERING: this used to fire immediately on payload
     receipt, before the listbox rows existed (buildCard/setListRows runs
     later, inside applySelection). The chip consumer (pdp-package-price.liquid
     renderNoPairNotice) filters every candidate through
     findPickerRowBySize(), which queries live `.pdp-gp-option` DOM nodes —
     with no rows built yet, EVERY candidate dropped and the notice fell
     back to text-only permanently (no re-dispatch ever followed to upgrade
     it). Fixed by calling this AFTER the row-build/selection step in every
     loadGroup exit branch that can build rows (see call sites below), never
     before. `lastGroupForAnnotations` is a single dedupe guard keyed to the
     group object identity (a fresh object every fetch) so the multiple exit
     branches below — sibling / <2-members / normal — can each call this
     defensively without risking a double-dispatch storm for the same
     payload; a vehicle change or refilter that reaches loadGroup again
     produces a NEW group object and re-dispatches as intended (keeps chip
     state fresh across refilters, per the fix design). `force` (used by the
     tfs:fitment-filter handler below) bypasses the guard for a SAME-group
     row rebuild — the showAll toggle re-runs setListRows via renderCard
     without a new /group fetch, so the chip consumer's findPickerRowBySize
     results can change (rows appear/disappear) even though `group` itself
     didn't. */
  var lastGroupForAnnotations = null;
  function dispatchPkgAnnotations(group, force) {
    if (!group || (!force && group === lastGroupForAnnotations)) return;
    lastGroupForAnnotations = group;
    var members = (group && group.members) || [];
    var out = [];
    for (var i = 0; i < members.length; i++) {
      var m = members[i];
      if (!m || !m.pkg) continue;
      out.push({
        pid: m.pid,
        size: rowSizeText(m),
        pkg: m.pkg
      });
    }
    try {
      document.dispatchEvent(new CustomEvent('tfs:pkg-annotations', {
        detail: { members: out }
      }));
    } catch (e) { /* CustomEvent unavailable — non-fatal */ }
  }

  function loadGroup(vehicleId, keepSelection) {
    fetchGroup(vehicleId).then(function (group) {
      if (!group || !group.members || !group.members.length) {
        failOpen();
        return;
      }
      /* Commit the vehicle context only WITH the payload it produced. A
         failed refetch (network/API) lands in failOpen, which keeps a live
         card serving the last good group — the old vehicleId must keep
         pairing with those old fit verdicts, or filtering/verdict-stamping
         runs new-vehicle state against stale data. */
      state.vehicleId = vehicleId || null;
      state.group = group;
      state.members = group.members;
      /* Giant-group mode scopes by DIAMETER chips — only meaningful when the
         members actually carry one (tire members don't; a giant tire group
         just renders the full scrollable listbox). */
      var hasDiameter = false;
      for (var gi = 0; gi < group.members.length; gi++) {
        var gm = group.members[gi];
        if (gm && gm.d != null) { hasDiameter = true; break; }
      }
      state.giant = !cfg.sibling && hasDiameter && group.members.length > GIANT_GROUP_THRESHOLD;
      if (!state.giant) state.diaScope = null;

      /* Vehicle context changed — any snapshot of the embed's result is stale. */
      embedResultSnapshot = undefined;

      /* Package-flow tier scoping (owner spec 2026-08-18): kick off the
         one-shot vehicle tire-tier fetch now that state.vehicleId is
         committed; rows re-render when it lands. No-op outside the flow. */
      loadPkgTireTiers();

      if (cfg.sibling) {
        /* Locked to the page product. Build the card even for a 1-member
           scope — the hub buy-box layout applies regardless of whether a
           staggered rear exists. A page product missing from its own group
           payload (stale index) keeps the static server-rendered page. */
        var locked = findMemberByPid(cfg.pid);
        if (!locked) {
          failOpen();
          return;
        }
        if (!built && !buildCard()) {
          failOpen();
          return;
        }
        /* Re-key the rear pick onto the fresh payload objects. */
        if (state.rearSel) state.rearSel = findMemberByPid(state.rearSel.pid);
        applySelection(locked, { initial: !keepSelection });
        /* Rows (the fixed row / no listbox on sibling pages, but the front
           listbox may still exist for other UI) are built by the calls
           above — dispatch now, post row-build. */
        dispatchPkgAnnotations(group);
        return;
      }

      if (group.members.length < 2) {
        /* Nothing to pick between — never adopt; keep the finishes rail. No
           listbox rows are ever built on this branch (nothing to list), so
           there is nothing for the chip consumer to find either way — still
           dispatch (per the "fires after every load" contract) so
           lastAnnotations updates and any stale chips from a PRIOR group
           get cleared/replaced on the consumer side. */
        if (!built) {
          while (root.firstChild) root.removeChild(root.firstChild);
          root.hidden = true;
        }
        state.sel = findMemberByPid(cfg.pid) || group.members[0];
        if (built) renderCard();
        renderFinishes();
        dispatchPkgAnnotations(group);
        return;
      }

      if (!built && !buildCard()) {
        failOpen();
        return;
      }

      var current = null;
      /* Filter-context preselection (owner bug report 2026-07-08) —
         selectPid / facet-param match, INITIAL LOAD ONLY (never on a
         keepSelection vehicle refetch, which must keep whatever the
         shopper has live-picked on the page). Takes precedence over the
         pre-existing pid/primary chain below; still subject to fit when a
         vehicle is active (resolveFilterContextSelection itself checks). */
      var viaFilterContext = false;
      if (!keepSelection) {
        var fc = resolveFilterContextSelection(!!state.vehicleId);
        if (fc) { current = fc; viaFilterContext = true; }
      }
      if (!current && keepSelection && state.sel) current = findMemberByPid(state.sel.pid);
      if (!current) current = findMemberByPid(cfg.pid) || group.members[0];

      /* Re-key the rear pick onto the fresh payload objects. */
      if (state.rearSel) state.rearSel = findMemberByPid(state.rearSel.pid);

      /* maybeAutoSelect must never silently shift AWAY from a selection
         just chosen via (a)/(b) above when that member already fits the
         active vehicle — it exists to rescue a NON-fitting default pick,
         not to second-guess a filter-context match that already passed its
         own fit check inside resolveFilterContextSelection. Skipping the
         call entirely (rather than just discarding a truthy result) also
         avoids bestFit picking a "better tier" sibling that isn't the one
         the shopper actually filtered/clicked to. */
      var auto = viaFilterContext ? null : maybeAutoSelect(current);
      if (auto) {
        /* Owner requirement: silently move to the best fitting sibling with
           ALL the normal selection side-effects. */
        applySelection(auto, {});
      } else {
        applySelection(current, { initial: !keepSelection });
      }
      /* applySelection → renderCard → setListRows has now built the
         `.pdp-gp-option` rows the chip consumer queries for — dispatch
         AFTER, not before (T9 anomaly 2). */
      dispatchPkgAnnotations(group);
    }).catch(function () {
      failOpen();
    });
  }

  function subscribeVehicleEvents() {
    var F = window.TFSFitment;
    if (!F || typeof F.on !== 'function') return false;
    F.on('vehicle-changed', function () {
      resolveVehicleId(function (vehicleId) {
        state.showAll = false;
        loadGroup(vehicleId, true);
      });
    });
    F.on('vehicle-cleared', function () {
      state.showAll = false;
      loadGroup(null, true);
    });
    /* Suspension setup chip changed on the same vehicle: /group verdicts
       depend on it (fitment-core attaches setup= to the fetch). */
    F.on('setup-changed', function () {
      resolveVehicleId(function (vehicleId) {
        loadGroup(vehicleId, true);
      });
    });
    return true;
  }

  /* Sibling "see all options" link (Stage 3 Task 1 item 2) — Liquid-rendered
     in main-product-merged.liquid (`.pdp-soc-alllink`, sibling pages only)
     with a static href to the canonical group URL; patched here rather than
     in Liquid since this file already owns forwarding for the finishes
     rail and the href never changes after render (no per-selection
     re-patch needed). No-op when the anchor isn't on the page (hub/wheel
     pages, or a sibling page whose current_variant is sold out — Liquid
     gates that separately) or when no package/vehicle params are present. */
  var siblingAllLink = document.querySelector('.pdp-soc-alllink');
  if (siblingAllLink && siblingAllLink.getAttribute('href')) {
    siblingAllLink.setAttribute('href', urlWithPackageContext(siblingAllLink.getAttribute('href')));
  }

  whenFitmentReady(function () {
    resolveVehicleId(function (vehicleId) {
      loadGroup(vehicleId, false);
    });

    if (!subscribeVehicleEvents()) {
      /* Timed out before fitment-core loaded — the fetch above went
         vehicle-less. Catch up once the core actually arrives. */
      document.addEventListener('tfs-fitment-ready', function () {
        subscribeVehicleEvents();
        resolveVehicleId(function (vehicleId) {
          if (vehicleId) loadGroup(vehicleId, true);
        });
      }, { once: true });
    }
  });

  /* Escape hatch replacing the removed "Show all sizes" toggle: the fitment
     widget (which owns ALL fit messaging) dispatches this to flip the
     vehicle hard filter. detail: { showAll: boolean }. Turning the filter
     back ON re-runs the auto-select so a non-fitting pick moves to the best
     fitting member with the normal selection side-effects. */
  document.addEventListener('tfs:fitment-filter', function (e) {
    var showAll = !!(e && e.detail && e.detail.showAll);
    if (showAll === state.showAll) return;
    state.showAll = showAll;
    if (!built) return;
    var auto = showAll ? null : maybeAutoSelect(state.sel);
    if (auto) applySelection(auto, {});
    else renderCard();
    /* Row rebuild without a new /group fetch (T9 anomaly 2 follow-through):
       setListRows just re-ran (inside applySelection's renderCard, or the
       renderCard() call directly above) against the same state.group, so
       the chip consumer's live-row lookup can flip for sizes newly shown
       or hidden by the filter toggle — force a re-dispatch so chip state
       stays fresh even though `group` itself is unchanged. */
    if (state.group) dispatchPkgAnnotations(state.group, true);
  });

  /* Back/forward restores the sibling that URL points at — the whole group
     is already loaded, so this is a pure client-side re-select. */
  window.addEventListener('popstate', function () {
    if (cfg.sibling || !state.group) return;
    var parts = window.location.pathname.split('/products/');
    if (parts.length < 2) return;
    var handle = parts[1].split('/')[0].split('?')[0];
    if (state.sel && state.sel.handle === handle) return;
    var m = findMemberByHandle(handle);
    if (m) applySelection(m, { fromPop: true });
  });
})();
